import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Modal, Input, Select } from 'antd';
import { Bell, ChevronDown, UserCheck, FileEdit, ClipboardCheck, BarChart3, Lock, BrainCircuit, Loader2, Calendar, Download } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useGoalsSettings } from '../../../contexts/GoalsSettingsContext';
import toast from 'react-hot-toast';
import * as goalsApi from '../../../services/goalsPerformanceApi';
import GoalsDashboard from '../../Goals/components/GoalsDashboard';
import PerformanceTrendsCard from '../../Goals/components/PerformanceTrendsCard';
import RecentActivityCard from '../../Goals/components/RecentActivityCard';
import GoalsScoreboard from '../../Goals/components/GoalsScoreboard';
import GoalsDetailView from '../../Goals/components/GoalsDetailView';
import ManagerView from '../../Goals/components/ManagerView';
import AddGoalModal from '../../Goals/components/AddGoalModal';
import AddAchievementModal from '../../Goals/components/AddAchievementModal';
import BadgeInfoModal from '../../Goals/components/BadgeInfoModal';
import StreakInfoModal from '../../Goals/components/StreakInfoModal';
import {
  GoalsTableSkeleton,
  SoftSkillsSkeleton,
  ReviewSummarySkeleton,
  GoalsDashboardSkeleton,
  TeamMembersSkeleton
} from '../../Goals/components/GoalsSkeleton';
import { motion, AnimatePresence } from 'framer-motion';
import { Target } from 'lucide-react';
import { PageShell } from '../components/PageShell';
import { Drawer } from '../components/Drawer';
import { Button as V2Button, Badge as V2Badge } from '../components/primitives';
import { Toggle } from '../components/Toggle';
import { useDashTheme } from '../hooks/useDashTheme';

const DEFAULT_REVIEW_QUESTIONS = [
  { id: 1, question: "Did you complete all your goals?", employee: 'no', manager: null },
  { id: 2, question: "Did your manager give more than 5 rating in all soft skills?", employee: null, manager: null },
  { id: 3, question: "Did you complete your critical goal?", employee: 'no', manager: null },
];

// FooterSlotPortal: renders an empty <div id="goals-cycle-footer-slot"> as a portal
// target into <body>, sized via the page scroll container's bounding rect so it lines up
// with main content and doesn't drift under a sidebar. Used as a fixed-position bottom
// bar so it stays pinned to the viewport regardless of how far the user has scrolled.
// GoalsTable.js detects this slot by id and portals its submit/compliance bar into it.
const FooterSlotPortal = ({ scrollRef, className }) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return undefined;
    const update = () => {
      const r = el.getBoundingClientRect();
      setRect({ left: r.left, width: r.width });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [scrollRef]);

  if (!rect || typeof document === 'undefined') return null;
  return createPortal(
    <div
      id="goals-cycle-footer-slot"
      className={className}
      style={{ left: rect.left, width: rect.width }}
    />,
    document.body
  );
};

const GoalsV2 = () => {
  const location = useLocation();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const { user } = useAuth();
  const { settings: goalsSettings } = useGoalsSettings();
  const t = useDashTheme();
  const scrollContainerRef = useRef(null);

  // View state
  const [showTeamMembers, setShowTeamMembers] = useState(false); // Show team members list
  const [isDetailView, setIsDetailView] = useState(false);
  const [activeTab, setActiveTab] = useState('myGoals'); // 'myGoals' or 'teamGoals'
  const isOrgAdmin = user?.role === 'org_admin' || user?.role === 'main_admin';
  const [aiGoalsDrawerOpen, setAiGoalsDrawerOpen] = useState(false);
  const [aiGoals, setAiGoals] = useState([]);
  const [aiGoalsLoading, setAiGoalsLoading] = useState(false);
  const [aiGoalsCycleId, setAiGoalsCycleId] = useState(null);
  const [isAutonomousMode, setIsAutonomousMode] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState('2026');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAchievementModalOpen, setIsAchievementModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [isReviewSummaryExpanded, setIsReviewSummaryExpanded] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [roadmapStartIndex, setRoadmapStartIndex] = useState(0);
  const [memberReportMonth, setMemberReportMonth] = useState(null);
  const [isManagerDetailView, setIsManagerDetailView] = useState(false);
  const [goalType, setGoalType] = useState('monthly');
  const [managerTab, setManagerTab] = useState('goals');
  const [searchQuery, setSearchQuery] = useState('');
  const [achievements, setAchievements] = useState([]);
  const [isBadgeInfoModalOpen, setIsBadgeInfoModalOpen] = useState(false);
  const [isStreakInfoModalOpen, setIsStreakInfoModalOpen] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, goalId: null, goalName: '', reason: '' });
  const [isRejectingGoal, setIsRejectingGoal] = useState(false);
  const [inlineNewGoal, setInlineNewGoal] = useState(null);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingGoalDraft, setEditingGoalDraft] = useState(null);

  // API data state
  const [loading, setLoading] = useState(true);
  const [softSkillsLoading, setSoftSkillsLoading] = useState(false);
  const [reviewSummaryLoading, setReviewSummaryLoading] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [currentCycle, setCurrentCycle] = useState(null);
  const [hasReportees, setHasReportees] = useState(
    user?.role === 'org_admin' || user?.role === 'main_admin'
  );
  const [teamMembersList, setTeamMembersList] = useState([]);
  const [hasIndirectReports, setHasIndirectReports] = useState(
    user?.role === 'org_admin' || user?.role === 'main_admin'
  );
  const [reporteeScope, setReporteeScope] = useState(
    (user?.role === 'org_admin' || user?.role === 'main_admin') ? 'all' : 'direct'
  ); // 'direct' | 'all'
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersPage, setTeamMembersPage] = useState(1);
  const [teamMembersPagination, setTeamMembersPagination] = useState({ total: 0, totalPages: 1 });
  const [teamMembersSearch, setTeamMembersSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [goalFilter, setGoalFilterState] = useState('all'); // 'all' | 'submitted' | 'not_submitted'
  const [submissionCounts, setSubmissionCounts] = useState({ all: 0, submitted: 0, notSubmitted: 0 });
  const setGoalFilter = (filter) => {
    setGoalFilterState(filter);
    setTeamMembersPage(1);
  };
  const [dashboardStats, setDashboardStats] = useState(null);
  const [allGoals, setAllGoals] = useState([]);
  const [allCycles, setAllCycles] = useState([]);
  const [refreshTeamMembersTrigger, setRefreshTeamMembersTrigger] = useState(0);
  const [projectsList, setProjectsList] = useState([]);
  // Scope-aware KPI stats — keyed by 'direct' | 'all' so toggling is instant after first fetch
  const [teamStatsByScope, setTeamStatsByScope] = useState({ direct: null, all: null });

  // Always show employee view (logged-in user's own dashboard)

  // Helper to handle member selection with proper state resets
  const handleTeamMemberSelect = async (member) => {
    setSelectedMember(member);

    // Fetch the true active cycle from the server to guarantee we don't use a stale/mutated month
    try {
      const response = await goalsApi.getCurrentCycle();
      if (response?.success && response?.data) {
        const cycleData = Array.isArray(response.data) ? response.data[0] : response.data;
        if (cycleData && cycleData.month) {
          setCurrentCycle(cycleData);
          setMemberReportMonth(cycleData.month);
        }
      }
    } catch (e) {
      console.error('Failed to reset to true active cycle on member select:', e);
      setMemberReportMonth(currentCycle?.month || null);
    }

    if (member) {
      setIsManagerDetailView(true);
    } else {
      setIsManagerDetailView(false);
    }
  };

  // Manager clicks "Review Completed" on a reportee's goals page during the review window.
  // Persists the completion stamp, toasts, then drops back to the team list view.
  const handleMarkReviewComplete = async () => {
    if (!currentCycle?._id || !selectedMember) return;
    const targetUserId = selectedMember?._id || selectedMember?.id;
    if (!targetUserId) return;
    setIsMarkingReviewComplete(true);
    try {
      const response = await goalsApi.markReviewComplete(currentCycle._id, targetUserId);
      if (response?.success) {
        setReviewCompletedAt(response?.data?.reviewCompletedAt || new Date().toISOString());
        toast.success(`Review completed for ${selectedMember.firstName || 'this user'}`);
        // Drop back to the team list
        setSelectedMember(null);
        setIsManagerDetailView(false);
        setRefreshTeamMembersTrigger(prev => prev + 1);
      } else {
        toast.error(response?.message || 'Failed to mark review complete');
      }
    } catch (error) {
      console.error('Error marking review complete:', error);
      const msg = error?.response?.data?.error?.message
        || error?.response?.data?.message
        || error?.message
        || 'Failed to mark review complete';
      toast.error(msg);
    } finally {
      setIsMarkingReviewComplete(false);
    }
  };

  // When currentCycle changes, ensure memberReportMonth is synced if not already
  useEffect(() => {
    if (currentCycle?.month && !memberReportMonth) {
      setMemberReportMonth(currentCycle.month);
    }
  }, [currentCycle]);

  // Check reportees on mount to determine view mode
  useEffect(() => {
    const checkReportees = async () => {
      try {
        const response = await goalsApi.checkReportees();
        if (response.success) {
          // Check if there are any direct or indirect reports
          const hasDirectReports = (response.data.directReports || []).length > 0;
          const hasIndirectReports = (response.data.indirectReports || []).length > 0;
          const hasAnyReportees = hasDirectReports || hasIndirectReports || response.data.hasReportees;

          setHasReportees(hasAnyReportees);
          setHasIndirectReports(hasIndirectReports);
          // Combine direct and indirect reports
          const allReports = [
            ...(response.data.directReports || []),
            ...(response.data.indirectReports || [])
          ].filter(member => (member._id || member.id) !== (user?.id || user?._id));
          setTeamMembersList(allReports);
        }
      } catch (error) {
        console.error('Error checking reportees:', error);
      }
    };

    if (user) {
      checkReportees();
    }
  }, [user]);

  // Helper function to transform member data to ManagerView format
  const transformMemberData = (member, index) => {
    // Generate avatar color based on name (consistent colors)
    const colors = [
      'bg-purple-600',
      'bg-green-600',
      'bg-amber-600',
      'bg-indigo-600',
      'bg-pink-600',
      'bg-blue-600',
      'bg-red-600',
      'bg-teal-600'
    ];
    const avatarColor = colors[index % colors.length];

    // Format goals count: show <approved>/<total> so mid-cycle progress is visible.
    // (evaluatedGoalsCount is still surfaced separately for the StatusStages icons.)
    const goalsText = member.totalGoals > 0
      ? `${member.goalsCount ?? 0}/${member.totalGoals}`
      : '0/0';

    // Format streak
    const streakText = member.streak > 0
      ? `${member.streak} ${member.streak === 1 ? 'Month' : 'Months'}`
      : '0 Month';

    // Format score (use currentScore if available, else avgScore)
    // Convert to string and ensure it's formatted correctly
    let score = '0.0';
    if (member.currentScore != null && member.currentScore !== undefined && member.currentScore !== '') {
      score = typeof member.currentScore === 'string' ? member.currentScore : (Math.floor(parseFloat(member.currentScore) * 10) / 10).toFixed(1);
    } else if (member.avgScore != null && member.avgScore !== undefined && member.avgScore !== '') {
      score = typeof member.avgScore === 'string' ? member.avgScore : (Math.floor(parseFloat(member.avgScore) * 10) / 10).toFixed(1);
    }

    // Check if review is completed (score > 0 means review is done)
    const scoreNum = parseFloat(score) || 0;
    const isReviewed = scoreNum > 0;

    return {
      id: member._id || member.id,
      _id: member._id || member.id,
      name: member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown',
      role: member.role || member.position || 'Employee',
      avatarColor: avatarColor,
      avatar: member.avatar || null,
      status: member.badge || 'Beginner',
      goals: goalsText,
      streak: streakText,
      score: score,
      reviewPending: member.reviewPending || false,
      isReviewed: isReviewed,
      hasBadge: member.hasBadge || false,
      goalsSubmitted: member.goalsSubmitted || false,
      totalGoals: member.totalGoals ?? 0,
      goalsCount: member.goalsCount ?? 0,
      evaluatedGoalsCount: member.evaluatedGoalsCount ?? 0,
      hasReportees: member.hasReportees === true,
      // Keep original data for reference
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      position: member.position
    };
  };

  // Fetch team members with goals data and dashboard stats
  useEffect(() => {
    const fetchTeamMembers = async () => {
      // If no reportees, don't fetch
      if (!hasReportees) {
        setTeamMembers([]);
        return;
      }

      // Allow fetch even without cycleId (will get basic member info)
      const cycleId = currentCycle?._id || null;

      try {
        console.log('=== Fetching Team Members ===');
        console.log('hasReportees:', hasReportees);
        console.log('currentCycle:', currentCycle);
        console.log('cycleId:', cycleId);
        console.log('user:', user?.id, user?.role);

        const response = await goalsApi.getTeamMembers(cycleId, teamMembersPage, 12, debouncedSearch, goalFilter, reporteeScope);
        console.log('Team members API response:', response);

        if (response && response.success) {
          console.log('Submission counts from API:', response.counts);
          if (response.counts) {
            setSubmissionCounts(response.counts);
          }
          if (response.pagination) {
            setTeamMembersPagination(response.pagination);
          }
          if (response.data && Array.isArray(response.data) && response.data.length > 0) {
            console.log('Team members data:', response.data);
            console.log('Team members count:', response.data.length);

            // Transform API data to match ManagerView format
            const transformedMembers = response.data
              .filter(member => (member._id || member.id) !== (user?.id || user?._id))
              .map((member, index) => {
                const transformed = transformMemberData(member, index);
                // Log each member's data for debugging
                console.log(`Member ${index + 1}:`, {
                  name: transformed.name,
                  goals: transformed.goals,
                  score: transformed.score,
                  streak: transformed.streak,
                  status: transformed.status,
                  originalData: {
                    goalsCount: member.goalsCount,
                    totalGoals: member.totalGoals,
                    currentScore: member.currentScore,
                    avgScore: member.avgScore,
                    streak: member.streak,
                    badge: member.badge
                  }
                });
                return transformed;
              });

            console.log('Transformed team members:', transformedMembers);
            console.log('Setting team members, count:', transformedMembers.length);
            setTeamMembers(transformedMembers);
            if (response.pagination) {
              setTeamMembersPagination(response.pagination);
            }
          } else {
            console.log('⚠️ Response.data is empty or not an array');
            // If search is active, show empty results - don't use fallback
            if (debouncedSearch) {
              setTeamMembers([]);
            } else if (teamMembersList && teamMembersList.length > 0) {
              const transformedMembers = teamMembersList
                .filter(member => (member._id || member.id) !== (user?.id || user?._id))
                .map((member, index) => transformMemberData({
                  _id: member._id,
                  id: member._id,
                  firstName: member.firstName,
                  lastName: member.lastName,
                  name: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
                  position: member.position,
                  email: member.email,
                  totalGoals: 0,
                  goalsCount: 0,
                  streak: 0,
                  avgScore: '0.0',
                  badge: 'Beginner',
                  reviewPending: false,
                  hasBadge: false
                }, index));
              console.log('Using fallback teamMembersList, transformed:', transformedMembers);
              setTeamMembers(transformedMembers);
            } else {
              console.log('No team members data available');
              setTeamMembers([]);
            }
          }
        } else {
          console.log('⚠️ API response not successful, using teamMembersList as fallback');
          // Fallback: Use teamMembersList if API fails
          if (teamMembersList && teamMembersList.length > 0) {
            const transformedMembers = teamMembersList
              .filter(member => (member._id || member.id) !== (user?.id || user?._id))
              .map((member, index) => transformMemberData({
                _id: member._id,
                id: member._id,
                firstName: member.firstName,
                lastName: member.lastName,
                name: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
                position: member.position,
                email: member.email,
                totalGoals: 0,
                goalsCount: 0,
                streak: 0,
                avgScore: '0.0',
                badge: 'Beginner',
                reviewPending: false,
                hasBadge: false
              }, index));
            console.log('Using fallback teamMembersList, transformed:', transformedMembers);
            setTeamMembers(transformedMembers);
          } else {
            setTeamMembers([]);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching team members:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Fallback: Use teamMembersList if API call fails
        if (teamMembersList && teamMembersList.length > 0) {
          const transformedMembers = teamMembersList
            .filter(member => (member._id || member.id) !== (user?.id || user?._id))
            .map((member, index) => transformMemberData({
              _id: member._id,
              id: member._id,
              firstName: member.firstName,
              lastName: member.lastName,
              name: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
              position: member.position,
              email: member.email,
              totalGoals: 0,
              goalsCount: 0,
              streak: 0,
              avgScore: '0.0',
              badge: 'Beginner',
              reviewPending: false,
              hasBadge: false
            }, index));
          console.log('Using fallback teamMembersList after error, transformed:', transformedMembers);
          setTeamMembers(transformedMembers);
        } else {
          setTeamMembers([]);
        }
      }
    };

    if (user && hasReportees) {
      fetchTeamMembers();
    }
  }, [hasReportees, currentCycle, user, teamMembersList, refreshTeamMembersTrigger, teamMembersPage, debouncedSearch, goalFilter, reporteeScope]);

  // Debounce search input
  useEffect(() => {
    setTeamMembersPage(1);
    const timer = setTimeout(() => {
      setDebouncedSearch(teamMembersSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [teamMembersSearch]);

  // Invalidate scope-aware KPI cache whenever any member's goals change
  useEffect(() => {
    setTeamStatsByScope({ direct: null, all: null });
  }, [refreshTeamMembersTrigger, currentCycle?._id]);

  // Fetch scope-aware KPI stats (lightweight aggregation, separate from paginated rows)
  useEffect(() => {
    if (!user || !hasReportees) return;
    const cycleId = currentCycle?._id || null;
    if (!cycleId) return;
    if (teamStatsByScope[reporteeScope]) return; // cached

    let cancelled = false;
    (async () => {
      try {
        const response = await goalsApi.getManagerTeamStats(cycleId, reporteeScope);
        if (!cancelled && response?.success && response?.data) {
          setTeamStatsByScope(prev => ({ ...prev, [reporteeScope]: response.data }));
        }
      } catch (err) {
        console.error('Error fetching team stats:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user, hasReportees, currentCycle, reporteeScope, teamStatsByScope]);


  // When allGoals loads for a selected member, recalculate their score using achievedPercent/10
  // and update their card in teamMembers so card and detail view stay in sync
  useEffect(() => {
    if (!selectedMember || !allGoals || allGoals.length === 0) return;

    const memberId = (selectedMember._id || selectedMember.id)?.toString();
    if (!memberId) return;

    const approvedGoals = allGoals.filter(g => {
      const status = (g.status || '').toString().trim().toUpperCase();
      return (status === 'APPROVED' || status === 'COMPLETED');
    });

    const goalsWithAchieved = approvedGoals.filter(g => {
      const achieved = g.achievedPercent || g.achieved || 0;
      return achieved > 0;
    });

    if (goalsWithAchieved.length === 0) return;

    let totalWeightedScore = 0;
    let totalWeight = 0;
    goalsWithAchieved.forEach(goal => {
      const achieved = goal.achievedPercent || goal.achieved || 0;
      const p = Math.max(0, Math.min(100, achieved));
      const goalScore = Math.round((p / 10) * 100) / 100;
      const weight = goal.weightage || goal.weight || 0;
      if (!isNaN(goalScore) && weight > 0) {
        totalWeightedScore += goalScore * weight;
        totalWeight += weight;
      }
    });

    if (totalWeight === 0) return;

    const recalculatedScore = (Math.round((totalWeightedScore / totalWeight) * 10) / 10).toFixed(1);

    // Bail out if the target member's score is already correct — returning the
    // same array reference lets React skip the re-render and avoids a feedback
    // loop with the goals-fetching effect (which produces a fresh allGoals array
    // each fetch and would otherwise re-trigger this effect indefinitely).
    setTeamMembers(prev => {
      const target = prev.find(m => ((m._id || m.id)?.toString() === memberId));
      if (!target) return prev;
      if (target.score === recalculatedScore) return prev;
      return prev.map(m => {
        const mId = (m._id || m.id)?.toString();
        if (mId === memberId) return { ...m, score: recalculatedScore };
        return m;
      });
    });
  }, [allGoals, selectedMember]);

  // Fetch current cycle
  useEffect(() => {
    const fetchCurrentCycle = async () => {
      try {
        console.log('=== Fetching Current Cycle ===');
        const response = await goalsApi.getCurrentCycle();
        console.log('📦 Full Response:', response);
        console.log('✅ Success:', response?.success);
        console.log('📊 Data:', response?.data);

        if (response?.success && response?.data) {
          // Handle case where data might be an array (shouldn't happen but just in case)
          const cycleData = Array.isArray(response.data) ? response.data[0] : response.data;

          console.log('🔄 Processing cycleData:', cycleData);
          console.log('📅 Month:', cycleData?.month);
          console.log('📌 Status:', cycleData?.status);

          if (cycleData && cycleData.month && cycleData.status) {
            console.log('✅ Setting state...');
            setCurrentCycle(cycleData);
            setSelectedMonth(cycleData.month);
            const year = cycleData.year;
            setSelectedYear(year != null ? year.toString() : new Date().getFullYear().toString());
            console.log('✅✅✅ Current cycle set successfully! Month:', cycleData.month, 'Status:', cycleData.status);
          } else {
            console.error('❌ Cycle data invalid:', { cycleData, hasMonth: !!cycleData?.month, hasStatus: !!cycleData?.status });
          }
        } else {
          console.error('❌ Invalid response:', { success: response?.success, hasData: !!response?.data });
        }
      } catch (error) {
        console.error('❌ Error fetching current cycle:', error);
        console.error('Error details:', error.response?.data || error.message);
        console.error('Error stack:', error.stack);
      }
    };

    if (user) {
      fetchCurrentCycle();
    }
  }, [user, goalsSettings]); // Refresh cycle when settings change

  // Fetch all cycles (fetch all cycles once, no need to filter by year on API)
  // We'll filter by year on frontend using useMemo
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const response = await goalsApi.getCycles();
        if (response.success) {
          setAllCycles(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching cycles:', error);
      }
    };

    if (user) {
      fetchCycles();
    }
  }, [user]);

  // Fetch projects for dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await goalsApi.getProjectsDropdown();
        if (response.success) {
          setProjectsList(response.data?.projects || []);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    if (user) {
      fetchProjects();
    }
  }, [user]);

  // Note: We fetch all cycles once and filter by selectedYear in useMemo
  // This is more efficient than making API calls on every year change

  // Fetch goals for current cycle
  useEffect(() => {
    const fetchGoals = async () => {
      if (!currentCycle?._id) return;

      // If in team members view but no member selected, don't fetch goals
      // Goals will be fetched when a member is selected
      if (showTeamMembers && !selectedMember) {
        setAllGoals([]);
        return;
      }

      // If in ManagerView (teamGoals tab) with memberReportMonth set, skip this fetch
      // The memberReportMonth useEffect will handle fetching data for that month
      if (activeTab === 'teamGoals' && selectedMember && memberReportMonth) {
        return;
      }

      try {
        setLoading(true);
        // Get target user ID - check both _id and id properties
        const targetUserId = selectedMember?._id || selectedMember?.id || user?.id || user?._id;

        // Validate targetUserId exists
        if (!targetUserId) {
          console.warn('No target user ID available for fetching goals');
          setAllGoals([]);
          return;
        }

        console.log('Fetching goals for user:', { selectedMember, targetUserId, isManager: hasReportees, showTeamMembers });
        const response = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
        if (response.success) {
          setAllGoals(response.data || []);
        } else {
          console.warn('Goals API returned unsuccessful response:', response);
          setAllGoals([]);
        }
      } catch (error) {
        console.error('Error fetching goals:', error);
        console.error('Error details:', error.response?.data || error.message);
        // Only show error toast if we have a valid user ID (not when just viewing team list)
        if (selectedMember || (!showTeamMembers && user?.id)) {
          toast.error(error.response?.data?.error?.message || 'Failed to load goals');
        }
        setAllGoals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [currentCycle, selectedMember, user, showTeamMembers, memberReportMonth]);

  // Fetch soft skills for current cycle
  useEffect(() => {
    const fetchSoftSkills = async () => {
      if (!currentCycle?._id) return;

      // If in ManagerView (teamGoals tab) with memberReportMonth set, skip this fetch
      // The memberReportMonth useEffect will handle fetching data for that month
      if (activeTab === 'teamGoals' && selectedMember && memberReportMonth) {
        return;
      }

      try {
        setSoftSkillsLoading(true);
        // Get target user ID - check both _id and id properties
        const targetUserId = selectedMember?._id || selectedMember?.id || user?.id;
        const isViewingTeamMember = selectedMember && selectedMember._id && selectedMember._id !== (user?.id || user?._id);

        const response = await goalsApi.getSoftSkills(currentCycle._id, targetUserId);
        if (response.success && response.data) {
          // Transform API data to component format
          // Backend now returns both employeeRatings and managerRatings
          const employeeRatings = response.data.employeeRatings || [];
          const managerRatings = response.data.managerRatings || [];

          const transformed = goalsSettings.softSkills.map(skill => {
            const employeeRating = employeeRatings.find(r => r.skillId === skill.id);
            const managerRating = managerRatings.find(r => r.skillId === skill.id);

            return {
              id: skill.id,
              name: skill.label,
              employeeRating: employeeRating?.rating || 0,
              managerRating: managerRating?.rating || 0
            };
          });
          setSoftSkillsData(transformed);
        } else {
          // If no data, initialize with zeros
          const transformed = goalsSettings.softSkills.map(skill => ({
            id: skill.id,
            name: skill.label,
            employeeRating: 0,
            managerRating: 0
          }));
          setSoftSkillsData(transformed);
        }
      } catch (error) {
        console.error('Error fetching soft skills:', error);
        // Initialize with zeros on error
        const transformed = goalsSettings.softSkills.map(skill => ({
          id: skill.id,
          name: skill.label,
          employeeRating: 0,
          managerRating: 0
        }));
        setSoftSkillsData(transformed);
      } finally {
        setSoftSkillsLoading(false);
      }
    };

    fetchSoftSkills();
  }, [currentCycle, selectedMember, user, goalsSettings, showTeamMembers, memberReportMonth]);

  // Fetch review summary for current cycle
  useEffect(() => {
    const fetchReviewSummary = async () => {
      if (!currentCycle?._id) return;

      // If in ManagerView (teamGoals tab) with memberReportMonth set, skip this fetch
      // The memberReportMonth useEffect will handle fetching data for that month
      if (activeTab === 'teamGoals' && selectedMember && memberReportMonth) {
        return;
      }

      try {
        // Get target user ID - check both _id and id properties
        const targetUserId = selectedMember?._id || selectedMember?.id || user?.id;
        const response = await goalsApi.getReviewSummary(currentCycle._id, targetUserId);
        if (response.success && response.data && response.data.questions && response.data.questions.length > 0) {
          setReviewSummary(response.data.questions);
        } else {
          setReviewSummary(DEFAULT_REVIEW_QUESTIONS);
        }
        setReviewCompletedAt(response?.data?.reviewCompletedAt || null);
      } catch (error) {
        console.error('Error fetching review summary:', error);
        setReviewCompletedAt(null);
      } finally {
        setReviewSummaryLoading(false);
      }
    };

    fetchReviewSummary();
  }, [currentCycle, selectedMember, user, showTeamMembers, memberReportMonth]);

  // Fetch data when memberReportMonth changes in ManagerView
  useEffect(() => {
    const fetchDataForMemberMonth = async () => {
      console.log('🔄 fetchDataForMemberMonth triggered:', {
        activeTab,
        selectedMember: !!selectedMember,
        memberReportMonth,
        allCyclesLength: allCycles?.length,
        allCycles: allCycles
      });

      // Only fetch if we're in ManagerView (teamGoals tab) with a selected member and memberReportMonth is set
      if (activeTab !== 'teamGoals' || !selectedMember || !memberReportMonth) {
        console.log('❌ Skipping fetch - conditions not met:', {
          activeTab,
          hasSelectedMember: !!selectedMember,
          hasMemberReportMonth: !!memberReportMonth
        });
        return;
      }

      // Wait for allCycles to be loaded
      if (!allCycles || allCycles.length === 0) {
        console.log('⏳ Waiting for allCycles to load...');
        return;
      }

      // Find cycle for the selected month (case-insensitive match, also check year if available)
      const selectedYearNum = parseInt(selectedYear) || new Date().getFullYear();
      const cycleForMonth = allCycles.find(c => {
        if (!c || !c.month) return false;

        const cycleMonth = (c.month || '').toString().trim();
        const reportMonth = (memberReportMonth || '').toString().trim();
        const monthMatch = cycleMonth.toLowerCase() === reportMonth.toLowerCase();

        // If cycle has year, also match year
        if (c.year != null) {
          const cycleYear = typeof c.year === 'number' ? c.year : parseInt(c.year);
          return monthMatch && cycleYear === selectedYearNum;
        }

        return monthMatch;
      });

      console.log('🔍 Looking for cycle:', {
        memberReportMonth,
        selectedYear: selectedYearNum,
        allCycles: allCycles.map(c => ({ month: c.month, year: c.year, _id: c._id })),
        cycleForMonth: cycleForMonth ? { month: cycleForMonth.month, year: cycleForMonth.year, _id: cycleForMonth._id } : null
      });

      if (!cycleForMonth?._id) {
        console.log('❌ No cycle found for month:', memberReportMonth, 'Available cycles:', allCycles.map(c => ({ month: c.month, year: c.year })));
        // If no cycle exists for this month, clear the data but don't show error
        // This allows viewing months that don't have cycles yet
        setCurrentCycle(null);
        setAllGoals([]);
        setSoftSkillsData(goalsSettings.softSkills.map(skill => ({
          id: skill.id,
          name: skill.label,
          employeeRating: 0,
          managerRating: 0
        })));
        setReviewSummary(DEFAULT_REVIEW_QUESTIONS);
        return;
      }

      console.log('✅ Fetching data for cycle:', cycleForMonth._id, 'month:', memberReportMonth);
      setCurrentCycle(cycleForMonth);

      try {
        const targetUserId = selectedMember?._id || selectedMember?.id;
        if (!targetUserId) {
          console.warn('No target user ID for fetching member month data');
          return;
        }

        // Fetch goals for this cycle
        setLoading(true);
        const goalsResponse = await goalsApi.getGoals({ cycleId: cycleForMonth._id, userId: targetUserId });
        if (goalsResponse.success) {
          setAllGoals(goalsResponse.data || []);
        } else {
          setAllGoals([]);
        }

        // Fetch soft skills for this cycle
        setSoftSkillsLoading(true);
        const softSkillsResponse = await goalsApi.getSoftSkills(cycleForMonth._id, targetUserId);
        if (softSkillsResponse.success && softSkillsResponse.data) {
          const employeeRatings = softSkillsResponse.data.employeeRatings || [];
          const managerRatings = softSkillsResponse.data.managerRatings || [];
          const transformed = goalsSettings.softSkills.map(skill => {
            const employeeRating = employeeRatings.find(r => r.skillId === skill.id);
            const managerRating = managerRatings.find(r => r.skillId === skill.id);
            return {
              id: skill.id,
              name: skill.label,
              employeeRating: employeeRating?.rating || 0,
              managerRating: managerRating?.rating || 0
            };
          });
          setSoftSkillsData(transformed);
        } else {
          const transformed = goalsSettings.softSkills.map(skill => ({
            id: skill.id,
            name: skill.label,
            employeeRating: 0,
            managerRating: 0
          }));
          setSoftSkillsData(transformed);
        }

        // Fetch review summary for this cycle
        setReviewSummaryLoading(true);
        const reviewResponse = await goalsApi.getReviewSummary(cycleForMonth._id, targetUserId);
        if (reviewResponse.success && reviewResponse.data && reviewResponse.data.questions && reviewResponse.data.questions.length > 0) {
          setReviewSummary(reviewResponse.data.questions);
        } else {
          setReviewSummary(DEFAULT_REVIEW_QUESTIONS);
        }
        setReviewCompletedAt(reviewResponse?.data?.reviewCompletedAt || null);
      } catch (error) {
        console.error('Error fetching data for member month:', error);
        setAllGoals([]);
        setSoftSkillsData([]);
        setReviewSummary(DEFAULT_REVIEW_QUESTIONS);
        setReviewCompletedAt(null);
      } finally {
        setLoading(false);
        setSoftSkillsLoading(false);
        setReviewSummaryLoading(false);
      }
    };

    fetchDataForMemberMonth();
  }, [memberReportMonth, selectedMember, activeTab, allCycles, goalsSettings]);

  // Fetch dashboard stats
  // Gamification benchmark: company-wide average score vs the user's own score.
  const [scoreBenchmark, setScoreBenchmark] = useState(null);
  useEffect(() => {
    goalsApi.getTeamStats()
      .then(res => { if (res?.success) setScoreBenchmark(res.data); })
      .catch(() => {});
  }, []);

  const fetchDashboardStats = useCallback(async () => {
    if (!user) return;
    try {
      // If viewing a member, get THEIR dashboard stats, else get current user's
      const targetUserId = (showTeamMembers && selectedMember)
        ? (selectedMember._id || selectedMember.id)
        : null;

      const response = await goalsApi.getDashboardStats(targetUserId);
      if (response.success) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  }, [user, showTeamMembers, selectedMember]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Banner config from current cycle
  // Calculate badge progress based on badgeRanges
  const calculateBadgeProgress = useMemo(() => {
    const badgeRangesList = goalsSettings?.badgeRanges || [];
    const currentBadgeLabel = dashboardStats?.badge || null;
    const avgScore = dashboardStats?.avgScore ? parseFloat(dashboardStats.avgScore) : null;

    // If no badge ranges or no current badge, return defaults
    if (!badgeRangesList.length || !currentBadgeLabel) {
      return {
        progressValue: "—",
        progressWidth: "0%",
        progressStart: "Beginner",
        progressLabel: "Rising Star Goal",
        nextBadge: null
      };
    }

    // Sort badge ranges by score (ascending: lowest to highest)
    // lessThan badges go first, then regular badges sorted by min value
    const sortedBadgeRanges = [...badgeRangesList].sort((a, b) => {
      // If one is lessThan, it should come first (lowest)
      if (a.lessThan && !b.lessThan) return -1;
      if (!a.lessThan && b.lessThan) return 1;
      // Both are same type, sort by min value
      return a.min - b.min;
    });

    // Find current badge index in sorted badgeRanges
    const currentBadgeIndex = sortedBadgeRanges.findIndex(range =>
      range.label.toLowerCase() === currentBadgeLabel.toLowerCase() ||
      currentBadgeLabel.toLowerCase().includes(range.label.toLowerCase()) ||
      range.label.toLowerCase().includes(currentBadgeLabel.toLowerCase())
    );

    // If current badge not found, return defaults
    if (currentBadgeIndex === -1) {
      return {
        progressValue: "—",
        progressWidth: "0%",
        progressStart: sortedBadgeRanges[0]?.label || "Beginner",
        progressLabel: sortedBadgeRanges[sortedBadgeRanges.length - 1]?.label || "Rising Star Goal",
        nextBadge: sortedBadgeRanges.length > 1 ? sortedBadgeRanges[1]?.label : null
      };
    }

    const totalBadges = sortedBadgeRanges.length;
    const currentPosition = currentBadgeIndex + 1; // 1-based position

    // Calculate progress percentage based on badge position
    // If on last badge, show 100%, otherwise show position-based percentage
    let progressPercentage;
    if (currentBadgeIndex === totalBadges - 1) {
      // Last badge - show 100% progress
      progressPercentage = 100;
    } else {
      // Not last badge - calculate based on position
      progressPercentage = Math.floor((currentPosition / totalBadges) * 100);
    }

    // Get current and next badge labels
    const progressStart = sortedBadgeRanges[currentBadgeIndex]?.label || "Beginner";
    // Next badge is the one at index + 1 (higher score)
    const nextBadge = currentBadgeIndex < totalBadges - 1
      ? sortedBadgeRanges[currentBadgeIndex + 1]?.label || null
      : null;
    const progressLabel = nextBadge || sortedBadgeRanges[totalBadges - 1]?.label || "Rising Star Goal";

    return {
      progressValue: `${currentPosition} / ${totalBadges}`,
      progressWidth: `${progressPercentage}%`,
      progressStart,
      progressLabel,
      nextBadge
    };
  }, [goalsSettings?.badgeRanges, dashboardStats?.badge, dashboardStats?.avgScore]);


  // Generate months array dynamically based on allCycles and selectedYear
  const months = useMemo(() => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    const selectedYearNum = parseInt(selectedYear) || new Date().getFullYear();

    // Get target user ID for score calculation
    const targetUserId = selectedMember?._id || selectedMember?.id || user?.id || user?._id;
    const targetUserIdStr = targetUserId?.toString();

    // Filter cycles for selected year
    const yearCycles = (allCycles || []).filter(cycle => {
      const cycleYear = typeof cycle.year === 'number' ? cycle.year : parseInt(cycle.year);
      return cycleYear === selectedYearNum;
    });

    // Create a map of month -> cycle for quick lookup
    const cycleMap = {};
    yearCycles.forEach(cycle => {
      if (cycle.month) {
        cycleMap[cycle.month] = cycle;
      }
    });

    // Helper function to check if all goals for a cycle are approved
    const checkAllGoalsApproved = (cycle) => {
      if (!cycle || !cycle._id || !targetUserIdStr) return false;
      const cycleIdStr = cycle._id?.toString() || cycle._id;
      const cycleGoals = (allGoals || []).filter(goal => {
        const goalCycleId = goal.cycleId?._id?.toString() || goal.cycleId?.toString() || goal.cycleId;
        if (goalCycleId !== cycleIdStr) return false;
        const goalUserId = goal.userId?._id?.toString() || goal.userId?.toString() || goal.userId;
        return goalUserId === targetUserIdStr;
      });
      if (cycleGoals.length === 0) return false;
      return cycleGoals.every(goal => goal.status === 'APPROVED');
    };

    // Build lookup from backend cycleScores — same calculation used for avgScore
    const backendCycleScoreMap = {};
    if (dashboardStats?.cycleScores) {
      dashboardStats.cycleScores.forEach(cs => {
        if (cs.month && cs.score != null) {
          backendCycleScoreMap[cs.month] = cs.score;
        }
      });
    }

    // Get score for a cycle using backend-calculated value (consistent with avgScore)
    const calculateCycleScore = (cycle, monthName) => {
      if (!cycle) return null;
      if (backendCycleScoreMap[monthName] != null) {
        return backendCycleScoreMap[monthName];
      }
      return null;
    };

    // Generate months array with status based on cycles
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-based (January = 0, February = 1, etc.)

    return monthNames.map(monthName => {
      const cycle = cycleMap[monthName];
      const monthIndex = monthNames.indexOf(monthName);

      if (!cycle) {
        // No cycle exists for this month
        // Check if this month is in the past or future
        const isPastMonth = selectedYearNum < currentYear ||
          (selectedYearNum === currentYear && monthIndex < currentMonth);

        if (isPastMonth) {
          // Past month without cycle - show as completed/locked
          return {
            name: monthName,
            status: 'completed',
            label: 'Locked',
            score: null,
            streak: false
          };
        } else {
          // Future month without cycle - show as upcoming
          return {
            name: monthName,
            status: 'upcoming',
            label: '',
            score: null,
            streak: false
          };
        }
      }

      // Cycle exists - determine status based on DATE WINDOWS first, then cycle.status as fallback
      // This ensures correct behavior even if cycle.status is stale (race condition between getCycles and getCurrentCycle)
      const now = new Date();

      // Check submission window using actual dates (primary check)
      // Dates are stored as IST-aligned UTC timestamps — compare raw UTC, no local timezone normalization needed
      let isSubmissionWindowActive = false;
      if (cycle.submissionStart && cycle.submissionEnd) {
        isSubmissionWindowActive = now >= new Date(cycle.submissionStart) && now <= new Date(cycle.submissionEnd);
      } else if (cycle.status === 'submission') {
        // Fallback: If status is 'submission' but dates are missing, consider it active
        isSubmissionWindowActive = true;
      }

      // Check review window using actual dates (primary check)
      let isReviewWindowActive = false;
      if (cycle.reviewStart && cycle.reviewEnd) {
        isReviewWindowActive = now >= new Date(cycle.reviewStart) && now <= new Date(cycle.reviewEnd);
      } else if (cycle.status === 'review') {
        // Fallback: If status is 'review' but dates are missing, consider it active
        isReviewWindowActive = true;
      }

      // Check evaluation window using actual dates
      let isEvaluationWindowActive = false;
      if (cycle.evaluationStart && cycle.evaluationEnd) {
        isEvaluationWindowActive = now >= new Date(cycle.evaluationStart) && now <= new Date(cycle.evaluationEnd);
      }

      let status = 'upcoming';
      let label = '';
      let score = null;

      // Determine status using DATE-BASED checks first (not cycle.status)
      // This fixes the race condition where cycle.status may be stale
      if (isSubmissionWindowActive) {
        // Currently within submission window dates
        status = 'active';
        label = 'Setting Goals';
        score = calculateCycleScore(cycle, monthName);
      } else if (isReviewWindowActive) {
        // Currently within review window dates
        status = 'active';
        label = 'Review Period';
        score = calculateCycleScore(cycle, monthName);
      } else if (isEvaluationWindowActive) {
        // Currently within evaluation window dates
        status = 'active';
        label = 'Evaluation';
        score = calculateCycleScore(cycle, monthName);
      } else if (cycle.status === 'finalized' || cycle.status === 'completed' || cycle.status === 'locked') {
        // Cycle finalized/completed/locked - cycle over
        status = 'completed';
        label = cycle.status === 'locked' ? 'Locked' : 'Report Ready';
        score = calculateCycleScore(cycle, monthName);
      } else {
        // All date windows have passed OR no dates available
        const isCurrentMonth = selectedYearNum === currentYear && monthIndex === currentMonth;

        if (isCurrentMonth) {
          // Current month is always visible/active regardless of window gaps
          status = 'active';
          label = 'Current Month';
          score = calculateCycleScore(cycle, monthName);
        } else {
          // Check if any window existed and is now past
          const hasAnyWindow = cycle.submissionEnd || cycle.reviewEnd || cycle.evaluationEnd;
          if (hasAnyWindow) {
            // Had windows but all are closed now
            status = 'completed';
            label = 'Cycle Over';
            score = calculateCycleScore(cycle, monthName);
          } else {
            // No window dates - use fallback based on month position
            const isPastMonth = selectedYearNum < currentYear ||
              (selectedYearNum === currentYear && monthIndex < currentMonth);
            status = isPastMonth ? 'completed' : 'upcoming';
            label = isPastMonth ? 'Cycle Over' : '';
            score = calculateCycleScore(cycle, monthName);
          }
        }
      }

      // Determine if this month card should be disabled for employee view:
      // Disabled when submission window is closed AND no goals exist for this cycle
      const cycleIdStr = cycle._id?.toString();
      const hasGoalsForCycle = (allGoals || []).some(goal => {
        const goalCycleId = goal.cycleId?._id?.toString() || goal.cycleId?.toString() || goal.cycleId;
        return goalCycleId === cycleIdStr;
      });

      // Also check if cycle has a score or is completed/finalized — that means goals existed
      const hasScoreOrCompleted = score != null ||
        cycle.status === 'completed' ||
        cycle.status === 'finalized' ||
        cycle.status === 'locked';

      const isSubmissionClosed = (() => {
        if (cycle.submissionStart && cycle.submissionEnd) {
          const now = new Date();
          return now < new Date(cycle.submissionStart) || now > new Date(cycle.submissionEnd);
        }
        // No dates: use status fallback
        return cycle.status !== 'submission';
      })();

      const disabled = isSubmissionClosed && !hasGoalsForCycle && !hasScoreOrCompleted;

      // Check if goals are submitted (all pending/approved goals have submittedAt)
      const cycleGoalsForUser = (allGoals || []).filter(goal => {
        const goalCycleId = goal.cycleId?._id?.toString() || goal.cycleId?.toString() || goal.cycleId;
        if (goalCycleId !== cycleIdStr) return false;
        const goalUserId = goal.userId?._id?.toString() || goal.userId?.toString() || goal.userId;
        const tuid = (selectedMember?._id || selectedMember?.id || user?.id || user?._id)?.toString();
        return goalUserId === tuid;
      });
      const pendingOrApproved = cycleGoalsForUser.filter(g => {
        const s = (g.status || '').toString().trim().toUpperCase();
        return s === 'PENDING' || s === 'APPROVED';
      });
      const goalsSubmitted = pendingOrApproved.length > 0 && pendingOrApproved.every(g => g.submittedAt != null);
      const hasRejectedGoals = cycleGoalsForUser.some(g => (g.status || '').toString().trim().toUpperCase() === 'REJECTED');

      return {
        name: monthName,
        status,
        label,
        score,
        allGoalsApproved: checkAllGoalsApproved(cycle),
        streak: false, // You can calculate streak here if needed
        disabled,
        goalsSubmitted,
        hasRejectedGoals
      };
    });
  }, [allCycles, selectedYear, allGoals, selectedMember, user, dashboardStats]);

  const [softSkillsData, setSoftSkillsData] = useState([
    { id: 1, name: 'Ownership', employeeRating: 3, managerRating: 10 },
    { id: 2, name: 'Growth attitude', employeeRating: 0, managerRating: 0 },
    { id: 3, name: 'Be Courageous', employeeRating: 0, managerRating: 0 },
    { id: 4, name: 'Be Frugal', employeeRating: 0, managerRating: 0 },
    { id: 5, name: 'Fight for the greater good', employeeRating: 0, managerRating: 0 },
  ]);

  const [reviewSummary, setReviewSummary] = useState(DEFAULT_REVIEW_QUESTIONS);
  const [reviewCompletedAt, setReviewCompletedAt] = useState(null);
  const [isMarkingReviewComplete, setIsMarkingReviewComplete] = useState(false);

  // teamMembers is now fetched dynamically from API (see useEffect above)

  // Goals are now fetched from API - allGoals state
  // Transform API goals to match component format
  const transformGoalForComponent = (goal) => ({
    id: goal._id || goal.id,
    name: goal.name,
    critical: goal.critical,
    isAiGoal: goal.isAiGoal === true,
    metric: goal.metric,
    weight: goal.weightage,
    weightage: goal.weightage,
    achieved: goal.achievedPercent || 0,
    achievedPercent: goal.achievedPercent,
    score: goal.score ? `${goal.score}/10` : null,
    status: goal.status,
    addedByManager: goal.addedByManager,
    createdBy: goal.createdBy, // Preserve createdBy object with firstName, lastName
    reviewedBy: goal.reviewedBy,
    userId: goal.userId, // Preserve userId for checking if it's own goal
    projectId: goal.projectId, // Preserve projectId object with _id and name
    submittedAt: goal.submittedAt, // Preserve submittedAt to check if goals are submitted
    editedSinceSubmit: goal.editedSinceSubmit === true,
    rejectionReason: goal.rejectionReason,
    reviewedAt: goal.reviewedAt,
    // Star/checklist flow fields
    employeeCompleted: goal.employeeCompleted ?? null,
    employeeComment: goal.employeeComment ?? null,
    managerVerified: goal.managerVerified ?? null,
  });

  // Get goals for selected month/cycle.
  // REJECTED goals are kept in the list so the employee sees manager feedback
  // and can edit/resubmit them. Limit checks (maxGoals) are enforced separately.
  const activeGoals = allGoals;

  // Determine which user's goals to show
  const targetUserId = selectedMember?._id || selectedMember?.id || user?.id;

  // Determine which month to show (manager detail view uses memberReportMonth, otherwise selectedMonth)
  const displayMonth = showTeamMembers && selectedMember ? memberReportMonth : selectedMonth;

  const currentMonthGoals = activeGoals
    .filter(goal => {
      // Filter by user (for manager view, show selected member's goals)
      // Handle userId as object (populated) or string
      let goalUserId = null;
      if (goal.userId) {
        if (typeof goal.userId === 'object' && goal.userId._id) {
          goalUserId = goal.userId._id?.toString();
        } else if (typeof goal.userId === 'string') {
          goalUserId = goal.userId;
        } else if (goal.userId != null && typeof goal.userId.toString === 'function') {
          try {
            goalUserId = goal.userId.toString();
          } catch (e) {
            console.warn('Error converting goal.userId to string:', e);
            goalUserId = String(goal.userId);
          }
        } else if (goal.userId != null) {
          goalUserId = String(goal.userId);
        }
      }

      const targetUserIdStr = targetUserId?.toString();
      if (!goalUserId || goalUserId !== targetUserIdStr) {
        return false;
      }

      // Filter by month/cycle
      if (!displayMonth || !currentCycle || !currentCycle._id) return false;
      const goalCycle = goal.cycleId;
      if (typeof goalCycle === 'object' && goalCycle.month) {
        return goalCycle.month === displayMonth;
      }
      const currentCycleId = currentCycle._id?.toString();
      const goalCycleId = goal.cycleId != null ? goal.cycleId.toString() : null;
      return currentCycle.month === displayMonth &&
        (goalCycleId === currentCycleId ||
          goal.cycleId === currentCycle._id);
    })
    .map(transformGoalForComponent);

  const detailViewGoals = currentMonthGoals;

  // Calculate employee average score from dashboard stats
  const employeeAvgScore = dashboardStats?.avgScore || null;

  // Check if goals cycle is editable (active cycle)
  // Uses DATE-BASED checks first (primary), then cycle.status as fallback
  // This fixes issues where cycle.status is stale but dates are correct
  // Calculate specific window status flags
  const { isSubmissionWindowActive, isReviewWindowActive, isEvaluationWindowActive } = useMemo(() => {
    console.log('🔍 [MyGoals] Calculating window status flags');
    if (!currentCycle) {
      console.log('❌ [MyGoals] No currentCycle - returning all false');
      return { isSubmissionWindowActive: false, isReviewWindowActive: false, isEvaluationWindowActive: false };
    }

    // IST date comparison: convert to IST by adding 5h 30m offset
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    const toISTDateStr = (date) => {
      const ist = new Date(new Date(date).getTime() + IST_OFFSET_MS);
      return ist.toISOString().split('T')[0]; // "YYYY-MM-DD"
    };
    const nowISTDateStr = toISTDateStr(new Date());
    console.log('🕐 [MyGoals] Current IST date:', nowISTDateStr);

    const checkWindow = (start, end) => {
      if (!start || !end) return false;
      const startISTDate = toISTDateStr(start);
      const endISTDate = toISTDateStr(end);
      const result = nowISTDateStr >= startISTDate && nowISTDateStr <= endISTDate;
      console.log('📅 [MyGoals] Window check (IST dates):', {
        startIST: startISTDate,
        endIST: endISTDate,
        nowIST: nowISTDateStr,
        result
      });
      return result;
    };

    const getWindowStatus = (start, end, status, activeStatus, windowName) => {
      const isWithinDates = start && end && checkWindow(start, end);
      console.log(`🔍 [MyGoals] ${windowName} window status:`, {
        hasStart: !!start,
        hasEnd: !!end,
        start: start ? new Date(start).toISOString() : null,
        end: end ? new Date(end).toISOString() : null,
        isWithinDates,
        cycleStatus: status
      });
      if (isWithinDates) {
        console.log(`✅ [MyGoals] ${windowName} window is ACTIVE (date-based)`);
        return true;
      }
      // Dates available but not within range — window is closed regardless of cycle.status
      if (start && end) {
        console.log(`❌ [MyGoals] ${windowName} window is INACTIVE (outside date range)`);
        return false;
      }
      // No dates stored — fall back to cycle.status only as last resort
      const statusMatch = status === activeStatus;
      console.log(`📊 [MyGoals] ${windowName} window status check (no-dates fallback):`, { status, activeStatus, statusMatch });
      return statusMatch;
    };

    const orgSubmissionOpen = getWindowStatus(currentCycle.submissionStart, currentCycle.submissionEnd, currentCycle.status, 'submission', 'Submission');
    const reopenUntil = currentCycle.submissionException?.reopenedUntil;
    const hasPersonalReopen = !!(reopenUntil && new Date(reopenUntil).getTime() >= Date.now());
    const result = {
      isSubmissionWindowActive: orgSubmissionOpen || hasPersonalReopen,
      isReviewWindowActive: getWindowStatus(currentCycle.reviewStart, currentCycle.reviewEnd, currentCycle.status, 'review', 'Review'),
      isEvaluationWindowActive: getWindowStatus(currentCycle.evaluationStart, currentCycle.evaluationEnd, currentCycle.status, 'evaluation', 'Evaluation')
    };

    console.log('📊 [MyGoals] Final window status flags:', result);
    return result;
  }, [currentCycle]);

  // bannerConfig depends on isSubmissionWindowActive — must be AFTER that useMemo
  const bannerConfig = currentCycle ? {
    title: isSubmissionWindowActive ? "Add Your Goals For" : "Goals For",
    subtitle: currentCycle.month,
    badge: `Active Cycle • ${currentCycle.month} ${currentCycle.year}`,
    rankLabel: dashboardStats?.badge || "Rising Star",
    progressLabel: calculateBadgeProgress.progressLabel,
    progressStart: calculateBadgeProgress.progressStart,
    progressValue: calculateBadgeProgress.progressValue,
    progressWidth: calculateBadgeProgress.progressWidth,
    currentStreak: dashboardStats?.streak ? `${dashboardStats.streak} ${dashboardStats.streak === 1 ? 'Month' : 'Months'}` : "0 Month"
  } : {
    title: "Add Your Goals For",
    subtitle: "Loading...",
    badge: "Loading...",
    rankLabel: "Rising Star",
    progressLabel: calculateBadgeProgress.progressLabel,
    progressStart: calculateBadgeProgress.progressStart,
    progressValue: calculateBadgeProgress.progressValue,
    progressWidth: calculateBadgeProgress.progressWidth,
    currentStreak: "0 Month"
  };

  // Whether the currently viewed month's cycle has already been evaluated (so we can show Soft Skills & Review Summary read-only)
  const isViewedMonthCycleEvaluated = useMemo(() => {
    const yearNum = selectedYear ? parseInt(selectedYear, 10) : new Date().getFullYear();
    const viewedMonth = (activeTab === 'teamGoals' && selectedMember) ? memberReportMonth : (selectedMonth || currentCycle?.month);
    if (!viewedMonth || !allCycles || allCycles.length === 0) return false;
    const monthStr = (viewedMonth || '').toString().trim();
    const cycleForViewedMonth = allCycles.find(c => {
      const cMonth = (c.month || '').toString().trim().toLowerCase();
      const cYear = c.year != null ? parseInt(c.year, 10) : null;
      return cMonth === monthStr.toLowerCase() && (cYear == null || cYear === yearNum);
    });
    if (!cycleForViewedMonth) return false;
    const now = new Date();
    const evalEndPassed = cycleForViewedMonth.evaluationEnd && now > new Date(cycleForViewedMonth.evaluationEnd);
    const statusFinal = ['completed', 'finalized', 'locked'].includes((cycleForViewedMonth.status || '').toLowerCase());
    return !!(evalEndPassed || statusFinal);
  }, [activeTab, selectedMember, memberReportMonth, selectedMonth, currentCycle?.month, selectedYear, allCycles]);

  // General flag for whether the cycle is "active" in some way
  const isGoalsCycleEditable = useMemo(() => {
    console.log('🔍 [MyGoals] Calculating isGoalsCycleEditable');

    // Org Admin in autonomous mode can ALWAYS edit the cycle, but ONLY for their own goals
    if (isOrgAdmin && isAutonomousMode && activeTab === 'myGoals') {
      console.log('✅ [MyGoals] isGoalsCycleEditable: true (Org Admin in autonomous mode)');
      return true;
    }

    if (!currentCycle) {
      console.log('❌ [MyGoals] isGoalsCycleEditable: false (no currentCycle)');
      return false;
    }

    // Check month match
    const currentCycleMonth = (currentCycle.month || '').toString().trim();
    const selectedMonthStr = (selectedMonth || '').toString().trim();
    if (selectedMonthStr && currentCycleMonth.toLowerCase() !== selectedMonthStr.toLowerCase()) {
      console.log('❌ [MyGoals] isGoalsCycleEditable: false (month mismatch)', {
        currentCycleMonth,
        selectedMonthStr
      });
      return false;
    }

    if (currentCycle.status === 'locked') {
      console.log('❌ [MyGoals] isGoalsCycleEditable: false (cycle is locked)');
      return false;
    }

    // Current logic: Editable if in ANY window OR fallback status
    if (isSubmissionWindowActive || isReviewWindowActive || isEvaluationWindowActive) {
      console.log('✅ [MyGoals] isGoalsCycleEditable: true (window is active)', {
        isSubmissionWindowActive,
        isReviewWindowActive,
        isEvaluationWindowActive
      });
      return true;
    }

    // Fallback if dates are missing
    const hasAnyDates = currentCycle.submissionStart || currentCycle.submissionEnd;
    if (!hasAnyDates && (currentCycle.status === 'submission' || currentCycle.status === 'review' || currentCycle.status === 'evaluation')) {
      console.log('✅ [MyGoals] isGoalsCycleEditable: true (fallback - no dates but status is active)', {
        status: currentCycle.status,
        hasAnyDates
      });
      return true;
    }

    console.log('❌ [MyGoals] isGoalsCycleEditable: false (all checks failed)', {
      status: currentCycle.status,
      isSubmissionWindowActive,
      isReviewWindowActive,
      isEvaluationWindowActive,
      hasAnyDates
    });
    return false;
  }, [currentCycle, selectedMonth, isSubmissionWindowActive, isReviewWindowActive, isEvaluationWindowActive, isOrgAdmin, isAutonomousMode, activeTab]);

  // Track currentCycle changes
  useEffect(() => {
    console.log('🔄 currentCycle state changed:', currentCycle);
    if (currentCycle) {
      console.log('✅ currentCycle is set! Month:', currentCycle.month, 'Status:', currentCycle.status);
    } else {
      console.log('⚠️ currentCycle is null');
    }
  }, [currentCycle]);

  // Debug logs for cycle editability
  useEffect(() => {
    console.log('=== Cycle Editability Debug ===');
    console.log('currentCycle:', currentCycle);
    console.log('selectedMonth:', selectedMonth);
    console.log('currentCycle?.status:', currentCycle?.status);
    console.log('currentCycle?.month:', currentCycle?.month);
    console.log('isGoalsCycleEditable:', isGoalsCycleEditable);
    console.log('hasReportees (isManager):', hasReportees);
  }, [currentCycle, selectedMonth, isGoalsCycleEditable, hasReportees]);

  const handleMonthClick = async (name) => {
    // Block navigation if month is disabled:
    // (submission window closed AND no goals added for this month)
    const clickedMonth = months.find(m => m.name === name);
    if (clickedMonth?.disabled) {
      return; // silently do nothing — card is visually disabled too
    }

    // Allow navigation to all months - cycle over check will be done in detail view
    // No need to block navigation based on past month or status

    // Find cycle for this month from allCycles
    const cycleForMonth = allCycles.find(c => c.month === name);

    // IMPORTANT: Prefer the existing currentCycle if it matches the clicked month
    // because currentCycle comes from getCurrentCycle API which has the most up-to-date
    // dates (after autoUpdateCycleStatus), while allCycles may have stale data
    const existingCurrentCycleMatchesMonth = currentCycle &&
      currentCycle.month === name &&
      currentCycle.year === (cycleForMonth?.year || new Date().getFullYear());

    const cycleToUse = existingCurrentCycleMatchesMonth ? currentCycle : cycleForMonth;

    // Set currentCycle FIRST to avoid timing issues with isGoalsCycleEditable
    if (cycleToUse) {
      // Set currentCycle so detail view can use it
      setCurrentCycle(cycleToUse);

      // Fetch goals for this cycle
      const cycleId = cycleToUse._id;
      if (cycleId) {
        try {
          const targetUserId = selectedMember?._id || user?.id;
          const response = await goalsApi.getGoals({ cycleId, userId: targetUserId });
          if (response.success) {
            setAllGoals(response.data || []);
          }
        } catch (error) {
          console.error('Error fetching goals for month:', error);
        }
      }
    } else {
      // If no cycle found for this month, set currentCycle to null
      setCurrentCycle(null);
      setAllGoals([]);
    }

    // Update selectedMonth AFTER currentCycle is set
    setSelectedMonth(name);
    setIsDetailView(true);
  };

  const handleNextRoadmap = () => {
    if (roadmapStartIndex + 4 < months.length) {
      setRoadmapStartIndex(prev => prev + 1);
    }
  };

  const handlePrevRoadmap = () => {
    if (roadmapStartIndex > 0) {
      setRoadmapStartIndex(prev => prev - 1);
    }
  };

  const handleReviewChange = async (id, type, value) => {
    if (!currentCycle?._id) return;

    const targetUserId = selectedMember?._id || user?.id;

    try {
      // Get current review summary
      const currentResponse = await goalsApi.getReviewSummary(currentCycle._id, targetUserId);
      let currentQuestions = (currentResponse.data && currentResponse.data.questions && currentResponse.data.questions.length > 0)
        ? currentResponse.data.questions
        : reviewSummary;

      // Ensure currentQuestions is an array and has all required fields
      if (!Array.isArray(currentQuestions) || currentQuestions.length === 0) {
        // If no questions exist, use the reviewSummary from state
        currentQuestions = reviewSummary.map(q => ({
          id: String(q.id),
          question: q.question || '',
          employee: q.employee || null,
          manager: q.manager || null
        }));
      }

      // Update the specific question - safely compare IDs
      const questionIndex = currentQuestions.findIndex(q => {
        if (!q || !q.id) return false;
        const qId = typeof q.id === 'string' ? q.id : (q.id != null ? String(q.id) : null);
        const searchId = typeof id === 'string' ? id : (id != null ? String(id) : null);
        return qId && searchId && qId === searchId;
      });

      if (questionIndex >= 0) {
        // Ensure value is lowercase 'yes' or 'no'
        const normalizedValue = value && typeof value === 'string' ? value.toLowerCase() : null;
        if (normalizedValue === 'yes' || normalizedValue === 'no') {
          currentQuestions[questionIndex][type] = normalizedValue;
        } else {
          currentQuestions[questionIndex][type] = null;
        }
      } else {
        // Question not found, log error
        console.error('Question not found:', id, currentQuestions);
        toast.error('Question not found');
        return;
      }

      // Ensure all questions have required fields before sending
      const validatedQuestions = currentQuestions.map(q => ({
        id: String(q.id || ''),
        question: String(q.question || ''),
        employee: (q.employee === 'yes' || q.employee === 'no') ? q.employee : null,
        manager: (q.manager === 'yes' || q.manager === 'no') ? q.manager : null
      }));

      // Save to backend
      await goalsApi.updateReviewSummary(currentCycle._id, targetUserId, validatedQuestions);

      // Update local state
      setReviewSummary(prev => prev.map(item =>
        item.id === id ? { ...item, [type]: value } : item
      ));

      toast.success('Review summary updated successfully');
    } catch (error) {
      console.error('Error updating review summary:', error);
      const errorMessage = error?.response?.data?.error?.message || error?.message || 'Failed to update review summary';
      toast.error(errorMessage);
    }
  };

  const handleSoftSkillRatingChange = async (skillId, ratingType, newRating) => {
    if (!currentCycle?._id) return;

    const targetUserId = selectedMember?._id || user?.id;
    const isViewingTeamMember = selectedMember && selectedMember._id && selectedMember._id !== (user?.id || user?._id);
    const isUpdatingOwnGoals = !isViewingTeamMember;

    // Validation: Check who can update what
    // Employee can only update employeeRating for themselves
    // Manager can only update managerRating for team members
    if (ratingType === 'employeeRating') {
      // Only allow if user is updating their own goals (not viewing team member)
      if (!isUpdatingOwnGoals) {
        toast.error('You can only update Employee Rating for your own goals');
        return;
      }
    } else if (ratingType === 'managerRating') {
      // Only allow if user is a manager AND viewing team member's goals
      if (!hasReportees || !isViewingTeamMember) {
        toast.error('You can only update Manager Rating for your team members');
        return;
      }
    }

    try {
      // Build ratings array from current softSkillsData, updating the changed one
      const backendRatings = [];

      softSkillsData.forEach(skill => {
        if (skill.id === skillId) {
          // Update the rating that changed
          backendRatings.push({ skillId: skill.id, rating: newRating });
        } else {
          // For other skills, send the appropriate rating based on context
          if (ratingType === 'employeeRating' && isUpdatingOwnGoals) {
            // When updating employee rating, send employee ratings for all skills
            if (skill.employeeRating > 0) {
              backendRatings.push({ skillId: skill.id, rating: skill.employeeRating });
            }
          } else if (ratingType === 'managerRating' && isViewingTeamMember) {
            // When updating manager rating, send manager ratings for all skills
            if (skill.managerRating > 0) {
              backendRatings.push({ skillId: skill.id, rating: skill.managerRating });
            }
          }
        }
      });

      // Save to backend
      await goalsApi.updateSoftSkills(currentCycle._id, targetUserId, backendRatings);

      // Update local state immediately for smooth UI
      setSoftSkillsData(prev => prev.map(skill =>
        skill.id === skillId ? { ...skill, [ratingType]: newRating } : skill
      ));

      toast.success('Soft skill rating updated successfully');
    } catch (error) {
      console.error('Error updating soft skill:', error);
      toast.error(error?.response?.data?.error?.message || 'Failed to update soft skill rating');
    }
  };

  const handleAddGoal = async (goalData) => {
    // If editing, use update handler
    if (editingGoal) {
      handleUpdateGoal(goalData);
      return;
    }

    if (!currentCycle?._id) {
      toast.error('No active cycle found');
      return;
    }

    try {
      // Get target user ID - check both _id and id properties
      const targetUserId = selectedMember?._id || selectedMember?.id || user?.id || user?._id;

      // Validate targetUserId exists
      if (!targetUserId) {
        toast.error('User ID is required. Please refresh the page and try again.');
        console.error('targetUserId is undefined:', { user, selectedMember });
        return;
      }

      console.log('Adding goal via modal for user:', {
        selectedMember,
        targetUserId,
        isManager: hasReportees,
        currentCycle: currentCycle._id
      });

      const goalPayload = {
        cycleId: currentCycle._id,
        name: goalData.title,
        critical: goalData.isCritical || false,
        metric: `${goalData.target}${goalData.unit}`,
        weightage: goalData.weightage ?? goalData.weight ?? 10, // User-provided; multiple of 10
        userId: targetUserId,
        projectId: goalData.projectId || null
      };

      const response = await goalsApi.createGoal(goalPayload);

      if (response.success) {
        // Refresh goals
        const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
        if (goalsResponse.success) {
          setAllGoals(goalsResponse.data || []);
        }

        setIsModalOpen(false);
        setEditingGoal(null);
        toast.success('Goal added successfully!');
      }
    } catch (error) {
      console.error('Error adding goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to add goal');
    }
  };

  const handleApproveGoal = async (goalId) => {
    // Prevent self-approval - extra safety check
    const goal = allGoals.find(g => (g._id || g.id) === goalId);
    let goalUserId = null;
    if (goal?.userId) {
      goalUserId = (typeof goal.userId === 'object' ? goal.userId._id : goal.userId)?.toString();
    }
    const currentUserId = (user?.id || user?._id)?.toString();

    if (goalUserId && goalUserId === currentUserId && !isAutonomousMode) {
      toast.error('You cannot approve your own goals');
      return;
    }

    try {
      const response = await goalsApi.approveGoal(goalId);
      if (response.success) {
        // Refresh goals
        if (currentCycle?._id) {
          const targetUserId = selectedMember?._id || user?.id;
          const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
          if (goalsResponse.success) {
            setAllGoals(goalsResponse.data || []);
          }
        }
        toast.success('Goal approved successfully!');
        if (hasReportees) {
          setRefreshTeamMembersTrigger(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Error approving goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to approve goal');
    }
  };

  // Star/checklist flow: employee marks own goal done/not done
  const handleSelfEvaluate = async (goalId, completed, comment = '') => {
    try {
      await goalsApi.selfEvaluateGoal(goalId, completed, comment);
      if (currentCycle?._id) {
        const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: user?.id || user?._id });
        if (goalsResponse.success) setAllGoals(goalsResponse.data || []);
      }
    } catch (error) {
      console.error('Error self-evaluating goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update goal');
    }
  };

  // Star flow: manager verifies (or rejects) the employee's completion claim
  const handleVerifyGoal = async (goalId, verified) => {
    try {
      await goalsApi.verifyGoal(goalId, verified);
      if (currentCycle?._id) {
        const targetId = selectedMember?._id || selectedMember?.id || user?.id;
        const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetId });
        if (goalsResponse.success) setAllGoals(goalsResponse.data || []);
      }
      if (hasReportees) setRefreshTeamMembersTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error verifying goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to verify goal');
    }
  };

  const handleRejectGoal = async (goalId) => {
    // Prevent self-rejection - extra safety check
    const goal = allGoals.find(g => (g._id || g.id) === goalId);
    let goalUserId = null;
    if (goal?.userId) {
      goalUserId = (typeof goal.userId === 'object' ? goal.userId._id : goal.userId)?.toString();
    }
    const currentUserId = (user?.id || user?._id)?.toString();

    if (goalUserId && goalUserId === currentUserId && !isAutonomousMode) {
      toast.error('You cannot reject your own goals');
      return;
    }

    // Open the reject-reason modal; submission happens in submitRejectModal.
    setRejectModal({ open: true, goalId, goalName: goal?.name || '', reason: '' });
  };

  const submitRejectModal = async () => {
    const { goalId, reason } = rejectModal;
    const trimmed = (reason || '').trim();
    if (!goalId) {
      setRejectModal({ open: false, goalId: null, goalName: '', reason: '' });
      return;
    }
    if (!trimmed) {
      toast.error('Please enter a reason for rejection');
      return;
    }
    setIsRejectingGoal(true);
    try {
      const response = await goalsApi.rejectGoal(goalId, trimmed);
      if (response.success) {
        if (currentCycle?._id) {
          const targetUserId = selectedMember?._id || user?.id;
          const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
          if (goalsResponse.success) {
            setAllGoals(goalsResponse.data || []);
          }
        }
        toast.success('Goal rejected');
        if (hasReportees) {
          setRefreshTeamMembersTrigger(prev => prev + 1);
        }
        setRejectModal({ open: false, goalId: null, goalName: '', reason: '' });
      }
    } catch (error) {
      console.error('Error rejecting goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to reject goal');
    } finally {
      setIsRejectingGoal(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      const response = await goalsApi.deleteGoal(goalId);
      if (response.success) {
        // Refresh goals
        if (currentCycle?._id) {
          const targetUserId = selectedMember?._id || user?.id;
          const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
          if (goalsResponse.success) {
            setAllGoals(goalsResponse.data || []);
          }
        }
        toast.success('Goal deleted successfully');
        if (hasReportees) {
          setRefreshTeamMembersTrigger(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to delete goal');
    }
  };

  const handleEditGoal = (goalId, updatedGoal = null) => {
    if (updatedGoal) {
      // Inline editing update
      setEditingGoalDraft(updatedGoal);
      return;
    }
    // Modal editing - use currentMonthGoals from API
    const goalToEdit = currentMonthGoals.find(g => (g.id === goalId || g._id === goalId));
    if (goalToEdit) {
      setEditingGoal(goalToEdit);
      setIsModalOpen(true);
    }
  };

  const handleSaveNewGoal = async (newGoalData) => {
    if (!newGoalData.name?.trim()) return;

    if (!currentCycle?._id) {
      toast.error('No active cycle found');
      return;
    }

    try {
      // Get target user ID - check both _id and id properties
      // For manager view, use selectedMember; for employee view, use logged-in user
      const targetUserId = (showTeamMembers && selectedMember)
        ? (selectedMember._id || selectedMember.id)
        : (user?.id || user?._id);

      // Validate targetUserId exists
      if (!targetUserId) {
        toast.error('User ID is required. Please refresh the page and try again.');
        console.error('targetUserId is undefined:', { user, selectedMember, showTeamMembers });
        return;
      }

      console.log('Adding goal for user:', {
        selectedMember,
        targetUserId,
        showTeamMembers,
        isManager: hasReportees,
        currentCycle: currentCycle._id,
        newGoalData
      });

      const goalPayload = {
        cycleId: currentCycle._id,
        name: newGoalData.name.trim(),
        critical: newGoalData.critical ?? false,
        metric: newGoalData.metric || '= 100%',
        weightage: newGoalData.weight || 0,
        userId: targetUserId,
        projectId: newGoalData.projectId || null
      };

      const response = await goalsApi.createGoal(goalPayload);

      if (response.success) {
        // Refresh goals
        const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
        if (goalsResponse.success) {
          setAllGoals(goalsResponse.data || []);
        }

        setInlineNewGoal(null);
        toast.success('Goal added successfully!');
      }
    } catch (error) {
      console.error('Error adding goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to add goal');
      throw error;
    }
  };

  const handleSaveEditGoal = async (goalId) => {
    if (!editingGoalDraft) return;

    try {
      const updatePayload = {
        name: editingGoalDraft.name,
        critical: editingGoalDraft.critical,
        metric: editingGoalDraft.metric,
        weightage: editingGoalDraft.weight || editingGoalDraft.weightage,
        projectId: editingGoalDraft.projectId || undefined
      };

      const response = await goalsApi.updateGoal(goalId, updatePayload);

      if (response.success) {
        // Refresh goals
        if (currentCycle?._id) {
          const targetUserId = selectedMember?._id || user?.id;
          const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
          if (goalsResponse.success) {
            setAllGoals(goalsResponse.data || []);
          }
        }

        setEditingGoalId(null);
        setEditingGoalDraft(null);
        toast.success('Goal updated successfully!');
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update goal');
    }
  };

  const handleCancelEdit = () => {
    setEditingGoalId(null);
    setEditingGoalDraft(null);
  };

  const handleToggleCritical = async (goalId, isCritical) => {
    try {
      // Critical is just a flag — never touch weightage on toggle.
      const response = await goalsApi.updateGoal(goalId, { critical: !!isCritical });
      if (response.success) {
        // Refresh goals
        if (currentCycle?._id) {
          const targetUserId = selectedMember?._id || user?.id;
          const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
          if (goalsResponse.success) {
            setAllGoals(goalsResponse.data || []);
          }
        }
        toast.success(isCritical ? 'Goal marked as critical' : 'Critical status removed');
      }
    } catch (error) {
      console.error('Error toggling critical status:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update critical status');
    }
  };

  // AI Goal toggle — only one AI Goal per user+cycle (backend clears the others).
  // Same refresh pattern as handleToggleCritical.
  const handleToggleAiGoal = async (goalId, isAiGoal) => {
    try {
      const response = await goalsApi.updateGoal(goalId, { isAiGoal: !!isAiGoal });
      if (response.success) {
        if (currentCycle?._id) {
          const targetUserId = selectedMember?._id || user?.id;
          const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
          if (goalsResponse.success) {
            setAllGoals(goalsResponse.data || []);
          }
        }
        toast.success(isAiGoal ? 'Goal marked as AI Goal' : 'AI Goal status removed');
      }
    } catch (error) {
      console.error('Error toggling AI goal status:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update AI goal status');
    }
  };

  // Only months that actually have AI goals (derived from fetched data).
  const aiGoalsCycleOptions = useMemo(() => {
    const MONTH_ORDER = {
      january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
      july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
    };
    const byId = new Map();
    (aiGoals || []).forEach((goal) => {
      const cycle = goal.cycleId;
      const id = cycle?._id || cycle;
      if (!id || byId.has(String(id))) return;
      const fallback = (allCycles || []).find((c) => String(c._id) === String(id));
      const month = cycle?.month || fallback?.month;
      const year = cycle?.year || fallback?.year;
      byId.set(String(id), {
        value: String(id),
        label: month ? `${month} ${year || ''}`.trim() : 'Unknown cycle',
        month,
        year,
      });
    });
    return [...byId.values()].sort((a, b) => {
      const yearDiff = (Number(b.year) || 0) - (Number(a.year) || 0);
      if (yearDiff !== 0) return yearDiff;
      return (MONTH_ORDER[(b.month || '').toLowerCase()] || 0) - (MONTH_ORDER[(a.month || '').toLowerCase()] || 0);
    });
  }, [aiGoals, allCycles]);

  const selectedAiGoalsCycle = useMemo(
    () => aiGoalsCycleOptions.find((c) => c.value === String(aiGoalsCycleId || '')) || null,
    [aiGoalsCycleOptions, aiGoalsCycleId]
  );

  const filteredAiGoals = useMemo(() => {
    if (!aiGoalsCycleId) return aiGoals;
    return (aiGoals || []).filter((g) => {
      const id = g.cycleId?._id || g.cycleId;
      return String(id) === String(aiGoalsCycleId);
    });
  }, [aiGoals, aiGoalsCycleId]);

  // Admin-only: load all AI goals once, then filter by month in the drawer.
  const fetchAiGoals = useCallback(async () => {
    setAiGoalsLoading(true);
    try {
      const response = await goalsApi.getAiGoals(null);
      if (response?.success) {
        const list = response.data || [];
        setAiGoals(list);

        // Prefer current cycle if it has AI goals; else first month that has any.
        const MONTH_ORDER = {
          january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
          july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
        };
        const cycleIds = [...new Set(list.map((g) => String(g.cycleId?._id || g.cycleId)).filter(Boolean))];
        const currentId = currentCycle?._id ? String(currentCycle._id) : null;
        if (currentId && cycleIds.includes(currentId)) {
          setAiGoalsCycleId(currentId);
        } else if (cycleIds.length > 0) {
          const sorted = cycleIds
            .map((id) => {
              const sample = list.find((g) => String(g.cycleId?._id || g.cycleId) === id)?.cycleId;
              return { id, month: sample?.month, year: sample?.year };
            })
            .sort((a, b) => {
              const yearDiff = (Number(b.year) || 0) - (Number(a.year) || 0);
              if (yearDiff !== 0) return yearDiff;
              return (MONTH_ORDER[(b.month || '').toLowerCase()] || 0) - (MONTH_ORDER[(a.month || '').toLowerCase()] || 0);
            });
          setAiGoalsCycleId(sorted[0].id);
        } else {
          setAiGoalsCycleId(null);
        }
      } else {
        setAiGoals([]);
        setAiGoalsCycleId(null);
        toast.error('Failed to load AI goals');
      }
    } catch (error) {
      console.error('Error loading AI goals:', error);
      setAiGoals([]);
      setAiGoalsCycleId(null);
      toast.error(error.response?.data?.error?.message || 'Failed to load AI goals');
    } finally {
      setAiGoalsLoading(false);
    }
  }, [currentCycle?._id]);

  const openAiGoalsDrawer = () => {
    setAiGoalsDrawerOpen(true);
    fetchAiGoals();
  };

  const handleAiGoalsCycleChange = (cycleId) => {
    setAiGoalsCycleId(cycleId);
  };

  const downloadAiGoalsCsv = () => {
    if (!filteredAiGoals.length) {
      toast.error('No AI goals to download');
      return;
    }
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Member', 'Email', 'Designation', 'Goal', 'Metric', 'Weightage %', 'Status', 'Project', 'Critical', 'Achieved %', 'Cycle'];
    const rows = filteredAiGoals.map((goal) => {
      const member = goal.userId || {};
      const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || 'Unknown';
      const cycle = goal.cycleId || {};
      const cycleLabel = cycle.month ? `${cycle.month} ${cycle.year || ''}`.trim() : (selectedAiGoalsCycle?.label || '');
      return [
        name,
        member.email || '',
        member.designation || member.position || member.department || '',
        goal.name || '',
        goal.metric || '',
        goal.weightage != null ? goal.weightage : '',
        goal.status || '',
        goal.projectId?.name || '',
        goal.critical ? 'Yes' : 'No',
        goal.achievedPercent != null ? goal.achievedPercent : '',
        cycleLabel,
      ].map(escape).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const stamp = new Date().toISOString().slice(0, 10);
    const monthPart = (selectedAiGoalsCycle?.label || 'all').replace(/\s+/g, '-');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-goals-${monthPart}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filteredAiGoals.length} AI goal${filteredAiGoals.length === 1 ? '' : 's'}`);
  };

  const handleSubmitGoals = async (cycleId, userId = null) => {
    if (!currentCycle?._id) {
      toast.error('No active cycle found');
      return;
    }

    try {
      const targetUserId = userId || user?.id || user?._id;
      if (!targetUserId) {
        toast.error('User ID is required');
        return;
      }

      // Bundle self-assessment soft skills with goal submission.
      // Only when employee is submitting their OWN goals during the submission window.
      const ownUserId = (user?.id || user?._id)?.toString();
      const isSelfSubmission = targetUserId.toString() === ownUserId;

      if (isSelfSubmission && isSubmissionWindowActive && softSkillsData.length > 0) {
        const ratedSkills = softSkillsData.filter(s => s.employeeRating > 0);
        const unratedCount = softSkillsData.length - ratedSkills.length;

        // Soft-block: warn if any soft skills are unrated, let user choose to continue.
        if (unratedCount > 0) {
          const proceed = await new Promise(resolve => {
            Modal.confirm({
              title: 'Some soft skills not rated',
              content: `You've rated ${ratedSkills.length} of ${softSkillsData.length} soft skills. Submit anyway?`,
              okText: 'Submit anyway',
              cancelText: 'Go back',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!proceed) return;
        }

        // Save self-assessment ratings first; abort goal submission if this fails.
        if (ratedSkills.length > 0) {
          try {
            const ratingsPayload = ratedSkills.map(s => ({ skillId: s.id, rating: s.employeeRating }));
            await goalsApi.updateSoftSkills(cycleId, targetUserId, ratingsPayload);
          } catch (err) {
            console.error('Error saving soft skills on submit:', err);
            toast.error('Failed to save soft skill ratings. Goals were not submitted.');
            return;
          }
        }
      }

      const response = await goalsApi.submitGoals(cycleId, targetUserId);

      if (response.success) {
        // Refresh goals to get updated submittedAt
        const goalsResponse = await goalsApi.getGoals({ cycleId, userId: targetUserId });
        if (goalsResponse.success) {
          const refreshedGoals = goalsResponse.data || [];
          // Verify submittedAt is in the response
          console.log('Goals after submission:', refreshedGoals.map(g => ({
            id: g._id || g.id,
            name: g.name,
            status: g.status,
            submittedAt: g.submittedAt
          })));
          setAllGoals(refreshedGoals);
        }
        const isManagerSubmitting = targetUserId.toString() !== (user?.id || user?._id)?.toString();
        toast.success(isManagerSubmitting
          ? 'Goals submitted successfully!'
          : 'Goals submitted successfully! Your manager has been notified.'
        );
      }
    } catch (error) {
      console.error('Error submitting goals:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to submit goals');
      throw error;
    }
  };

  const handleUpdateGoal = async (goalData) => {
    if (!editingGoal) return;

    if (!currentCycle?._id) {
      toast.error('No active cycle found');
      return;
    }

    try {
      const goalId = editingGoal._id || editingGoal.id;
      const updatePayload = {
        name: goalData.title,
        critical: goalData.isCritical || false,
        metric: `${goalData.target}${goalData.unit}`,
        weightage: goalData.target || 0
      };

      const response = await goalsApi.updateGoal(goalId, updatePayload);

      if (response.success) {
        // Refresh goals
        const targetUserId = selectedMember?._id || user?.id;
        const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
        if (goalsResponse.success) {
          setAllGoals(goalsResponse.data || []);
        }

        setEditingGoal(null);
        setIsModalOpen(false);
        toast.success('Goal updated successfully!');
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to update goal');
    }
  };

  const handleAddAchievement = (achievementData) => {
    const newAchievement = {
      id: Date.now(),
      ...achievementData,
      memberId: selectedMember?.id || null,
      month: selectedMember ? memberReportMonth : (selectedMonth || currentCycle?.month),
    };
    setAchievements(prev => [...prev, newAchievement]);
    setIsAchievementModalOpen(false);
    toast.success('Achievement added successfully!');
  };

  // V2 shell tab change — preserves the original tab-switch reset logic.
  const handleTabChange = (key) => {
    if (key === 'myGoals') {
      setActiveTab('myGoals');
      setShowTeamMembers(false);
      setSelectedMember(null);
      setIsDetailView(false);
      setManagerTab('goals');
      setMemberReportMonth(null);
      setIsManagerDetailView(false);
      (async () => {
        try {
          setLoading(true);
          const response = await goalsApi.getCurrentCycle();
          if (response?.success && response?.data) {
            const cycleData = Array.isArray(response.data) ? response.data[0] : response.data;
            if (cycleData && cycleData.month && cycleData.status) {
              setCurrentCycle(cycleData);
              setSelectedMonth(cycleData.month);
              const year = cycleData.year;
              setSelectedYear(year != null ? year.toString() : new Date().getFullYear().toString());
            }
          }
        } catch (error) {
          console.error('Error resetting to my cycle:', error);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setActiveTab('teamGoals');
      setShowTeamMembers(true);
      setSelectedMember(null);
      setIsDetailView(false);
      setManagerTab('member');
    }
  };

  // My Goals / Team Goals now live in the sidebar, so the route is the source
  // of truth for which view is showing.
  const routeTab = location.pathname.endsWith('/goals/team') ? 'teamGoals' : 'myGoals';
  useEffect(() => {
    if (routeTab !== activeTab) handleTabChange(routeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeTab]);

  return (
    <PageShell
      eyebrow="Performance · Goals"
      title="Goals"
      subtitle={currentCycle ? `Current cycle · ${currentCycle.month || ''} ${currentCycle.year || ''}`.trim() : 'Set, track and achieve your goals'}
      icon={<Target size={22} />}
      actions={isOrgAdmin ? (
        <>
          {activeTab === 'myGoals' && (
            <div
              title="Ignore the cycle schedule so you can add or edit goals anytime (Org Admin only)"
              style={{
                display: 'flex', alignItems: 'center', gap: t.s[2], height: 32,
                padding: `0 ${t.s[3]}px`, border: `1px solid ${t.c.border}`,
                borderRadius: t.r.sm, background: t.c.surface,
              }}
            >
              <span style={{ fontSize: t.font.size.sm, fontWeight: t.font.weight.medium, color: t.c.textSecondary, whiteSpace: 'nowrap' }}>
                Unlock editing
              </span>
              <Toggle
                on={isAutonomousMode}
                onClick={() => setIsAutonomousMode(v => !v)}
                label="Unlock editing"
              />
            </div>
          )}
          <V2Button variant="secondary" leftIcon={<BrainCircuit size={15} />} onClick={openAiGoalsDrawer}>
            AI Goals
          </V2Button>
        </>
      ) : null}
    >
      <div
        ref={scrollContainerRef}
        className={`text-${isDark ? 'white' : 'slate-900'}`}
        style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
      >
      {/* Main Content */}
      <section className="flex-1">
        <div className="w-full">
          {/* (Original in-page tab switcher removed — replaced by V2 PageShell tabs) */}

          {activeTab === 'teamGoals' && (() => {
            // Windowless modes (score_only / checklist) never gate on windows,
            // so show a single always-open strip instead of window phases.
            const flowMode = goalsSettings?.goalsMode || 'classic';
            const activeWindow = flowMode === 'checklist'
              ? { label: 'Checklist mode', sub: 'Goals can be added and checked off anytime', accent: 'green', Icon: ClipboardCheck, endDate: null }
              : flowMode === 'score_only'
                ? { label: 'Continuous mode', sub: 'Goals can be added and scored anytime', accent: 'blue', Icon: BarChart3, endDate: null }
                : isSubmissionWindowActive
                  ? { label: 'Adding goals', sub: 'Your team is adding & submitting goals for this month', accent: 'blue', Icon: FileEdit, endDate: currentCycle?.submissionEnd }
                  : isReviewWindowActive
                    ? { label: 'Approving goals', sub: 'Approve or send back the goals your team submitted', accent: 'orange', Icon: ClipboardCheck, endDate: currentCycle?.reviewEnd }
                    : isEvaluationWindowActive
                      ? { label: 'Scoring', sub: 'Set what each person achieved this month', accent: 'green', Icon: BarChart3, endDate: currentCycle?.evaluationEnd }
                      : { label: 'Between phases', sub: 'Nothing needs your action right now', accent: null, Icon: Lock, endDate: null };
            const Icon = activeWindow.Icon;
            const pair = activeWindow.accent ? t.accentPairs[activeWindow.accent] : null;
            const isLive = !!activeWindow.accent;
            const daysLeft = activeWindow.endDate
              ? Math.max(0, Math.ceil((new Date(activeWindow.endDate) - new Date()) / (1000 * 60 * 60 * 24)))
              : null;
            return (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: t.s[3], marginBottom: t.s[4],
                padding: `${t.s[2]}px ${t.s[3]}px`, background: t.c.surface,
                border: `1px solid ${t.c.border}`, borderRadius: t.r.lg, boxShadow: t.shadow.sm,
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 26, height: 26, borderRadius: t.r.sm, flexShrink: 0,
                  background: pair ? pair.bg : t.c.surfaceMuted,
                  color: pair ? pair.fg : t.c.textTertiary,
                }}>
                  <Icon size={14} strokeWidth={2.2} />
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.text }}>
                      {activeWindow.label}
                    </span>
                    {isLive && <V2Badge tone="positive" dot>Live</V2Badge>}
                  </span>
                  <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>{activeWindow.sub}</span>
                </span>
                {daysLeft !== null && (
                  <span style={{
                    display: 'flex', flexDirection: 'column', paddingLeft: t.s[3],
                    borderLeft: `1px solid ${t.c.border}`, whiteSpace: 'nowrap',
                  }}>
                    <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
                      {daysLeft === 0 ? 'Closes' : 'Closes in'}
                    </span>
                    <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.semibold, color: daysLeft <= 3 ? t.status.negative : t.c.text, fontVariantNumeric: 'tabular-nums' }}>
                      {daysLeft === 0 ? 'Today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
                    </span>
                  </span>
                )}
              </div>
            );
          })()}

          {activeTab === 'teamGoals' ? (
            // Team Members View
            selectedMember ? (
              // Individual Member Detail View
              <ManagerView
                softSkillsEnabled={goalsSettings?.softSkillsEnabled !== false}
                isViewedMonthCycleEvaluated={isViewedMonthCycleEvaluated}
                managerTab={managerTab}
                setManagerTab={setManagerTab}
                teamMembers={teamMembers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                goalFilter={goalFilter}
                setGoalFilter={setGoalFilter}
                submissionCounts={submissionCounts}
                selectedMember={selectedMember ? {
                  ...selectedMember,
                  // Sync streak and status (rank) from dashboardStats if available
                  streak: dashboardStats?.streak !== undefined
                    ? `${dashboardStats.streak} ${dashboardStats.streak === 1 ? 'Month' : 'Months'}`
                    : selectedMember.streak,
                  status: dashboardStats?.badge || selectedMember.status
                } : null}
                setSelectedMember={handleTeamMemberSelect}
                memberReportMonth={memberReportMonth}
                setMemberReportMonth={setMemberReportMonth}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                months={months}
                roadmapStartIndex={roadmapStartIndex}
                onPrevRoadmap={handlePrevRoadmap}
                onNextRoadmap={handleNextRoadmap}
                onMonthClick={(name) => {
                  // Allow clicking on all months (upcoming months are already blocked in ManagerView)
                  setMemberReportMonth(name);
                  setIsManagerDetailView(true);
                }}
                isManagerDetailView={isManagerDetailView}
                setIsManagerDetailView={setIsManagerDetailView}
                currentMonthGoals={currentMonthGoals}
                softSkillsData={softSkillsData}
                reviewSummary={reviewSummary}
                isReviewSummaryExpanded={isReviewSummaryExpanded}
                onToggleReviewSummary={() => setIsReviewSummaryExpanded(!isReviewSummaryExpanded)}
                onReviewChange={handleReviewChange}
                onAddGoal={() => setIsModalOpen(true)}
                onAddAchievement={() => setIsAchievementModalOpen(true)}
                onApproveGoal={handleApproveGoal}
                onRejectGoal={handleRejectGoal}
                onEditGoal={handleEditGoal}
                onDeleteGoal={handleDeleteGoal}
                managerUserId={user?.id || user?._id || null}
                loading={loading}
                softSkillsLoading={softSkillsLoading}
                reviewSummaryLoading={reviewSummaryLoading}
                onSetAchievedPercent={async (goalId, achievedPercent) => {
                  try {
                    await goalsApi.setAchievedPercent(goalId, achievedPercent);

                    // Refresh stats immediately after evaluation
                    fetchDashboardStats();

                    if (currentCycle?._id) {
                      const targetUserId = selectedMember?._id || user?.id;
                      const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
                      if (goalsResponse.success) {
                        setAllGoals(goalsResponse.data || []);
                      }
                    }
                    // Refresh team list to update score on cards
                    if (hasReportees) {
                      setRefreshTeamMembersTrigger(prev => prev + 1);
                    }
                  } catch (error) {
                    console.error('Error setting achieved percent:', error);
                    toast.error('Failed to update achieved percentage');
                  }
                }}
                onCalculateScore={async (cycleId, userId) => {
                  try {
                    const response = await goalsApi.calculateScore(cycleId, userId);
                    if (response.success) {
                      console.log('Final score calculated:', response.data);
                      // Refresh goals to get updated scores
                      const goalsResponse = await goalsApi.getGoals({ cycleId, userId });
                      if (goalsResponse.success) {
                        setAllGoals(goalsResponse.data || []);
                      }
                      // Refresh team members to update scores in Team Member Report
                      if (hasReportees) {
                        setRefreshTeamMembersTrigger(prev => prev + 1);
                      }
                      // After a manager evaluates a team member, drop back to the
                      // team list — same UX as Mark Review Complete.
                      const ownId = (user?.id || user?._id)?.toString();
                      const evaluatedId = userId?.toString();
                      if (selectedMember && evaluatedId && evaluatedId !== ownId) {
                        setSelectedMember(null);
                        setIsManagerDetailView(false);
                        setRefreshTeamMembersTrigger(prev => prev + 1);
                      }
                    }
                  } catch (error) {
                    console.error('Error calculating score:', error);
                    throw error;
                  }
                }}
                cycleId={currentCycle?._id || null}
                targetUserId={selectedMember?._id || selectedMember?.id || user?.id || user?._id || null}
                currentCycle={currentCycle}
                bannerConfig={bannerConfig}
                onSoftSkillRatingChange={handleSoftSkillRatingChange}
                canEditEmployeeRating={false}
                canEditManagerRating={((selectedMember && selectedMember._id && selectedMember._id !== (user?.id || user?._id)) && isEvaluationWindowActive) || (isOrgAdmin && isAutonomousMode)}
                canEditEmployeeResponse={false}
                canEditManagerResponse={((selectedMember && selectedMember._id && selectedMember._id !== (user?.id || user?._id)) && isEvaluationWindowActive) || (isOrgAdmin && isAutonomousMode)}
                achievements={achievements.filter(a =>
                  selectedMember ? a.memberId === selectedMember.id && a.month === memberReportMonth : a.month === (selectedMonth || currentCycle?.month)
                )}
                minGoals={goalsSettings?.minGoals || 0}
                maxGoals={goalsSettings?.maxGoals || 0}
                criticalRequired={goalsSettings?.criticalRequired || 0}
                minWeightage={goalsSettings?.minWeightage ?? 10}
                maxWeightage={goalsSettings?.maxWeightage ?? 100}
                inlineNewGoal={inlineNewGoal}
                setInlineNewGoal={setInlineNewGoal}
                editingGoalId={editingGoalId}
                setEditingGoalId={setEditingGoalId}
                onSaveNewGoal={handleSaveNewGoal}
                onSaveEditGoal={handleSaveEditGoal}
                onCancelEdit={handleCancelEdit}
                onSubmitGoals={handleSubmitGoals}
                scoreRanges={goalsSettings.scoreRanges}
                isSubmissionWindowActive={isSubmissionWindowActive}
                isReviewWindowActive={isReviewWindowActive}
                isEvaluationWindowActive={isEvaluationWindowActive}
                isOrgAdmin={isOrgAdmin}
                badgeRanges={goalsSettings?.badgeRanges || []}
                streakIconUrl={goalsSettings?.streakPoints?.icon?.url || null}
                onToggleCritical={handleToggleCritical}
                aiGoalEnabled={!!goalsSettings?.aiGoalEnabled}
                onToggleAiGoal={handleToggleAiGoal}
                allCycles={allCycles}
                projectsList={projectsList}
                cycleScoringMode={currentCycle?.scoringMode || 'legacy'}
                reviewCompletedAt={reviewCompletedAt}
                onMarkReviewComplete={handleMarkReviewComplete}
                isMarkingReviewComplete={isMarkingReviewComplete}
                onSelfEvaluate={handleSelfEvaluate}
                onVerifyGoal={handleVerifyGoal}
              />
            ) : (
              // Team Members List
              <ManagerView
                softSkillsEnabled={goalsSettings?.softSkillsEnabled !== false}
                isViewedMonthCycleEvaluated={isViewedMonthCycleEvaluated}
                managerTab={managerTab}
                setManagerTab={setManagerTab}
                teamMembers={teamMembers}
                teamStats={teamStatsByScope[reporteeScope]}
                reporteeScope={reporteeScope}
                setReporteeScope={setReporteeScope}
                hasIndirectReports={hasIndirectReports}
                searchQuery={teamMembersSearch}
                setSearchQuery={setTeamMembersSearch}
                goalFilter={goalFilter}
                setGoalFilter={setGoalFilter}
                submissionCounts={submissionCounts}
                selectedMember={null}
                setSelectedMember={handleTeamMemberSelect}
                isOrgAdmin={isOrgAdmin}
                memberReportMonth={memberReportMonth}
                setMemberReportMonth={setMemberReportMonth}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                months={months}
                roadmapStartIndex={roadmapStartIndex}
                onPrevRoadmap={handlePrevRoadmap}
                onNextRoadmap={handleNextRoadmap}
                onMonthClick={(name) => {
                  // Allow clicking on all months (upcoming months are already blocked in ManagerView)
                  setMemberReportMonth(name);
                  setIsManagerDetailView(true);
                }}
                isManagerDetailView={isManagerDetailView}
                setIsManagerDetailView={setIsManagerDetailView}
                currentMonthGoals={currentMonthGoals}
                softSkillsData={softSkillsData}
                reviewSummary={reviewSummary}
                isReviewSummaryExpanded={isReviewSummaryExpanded}
                onToggleReviewSummary={() => setIsReviewSummaryExpanded(!isReviewSummaryExpanded)}
                onReviewChange={handleReviewChange}
                onAddGoal={() => setIsModalOpen(true)}
                onAddAchievement={() => setIsAchievementModalOpen(true)}
                onApproveGoal={handleApproveGoal}
                onRejectGoal={handleRejectGoal}
                onEditGoal={handleEditGoal}
                onDeleteGoal={handleDeleteGoal}
                managerUserId={user?.id || user?._id || null}
                loading={loading}
                softSkillsLoading={softSkillsLoading}
                reviewSummaryLoading={reviewSummaryLoading}
                onSetAchievedPercent={async (goalId, achievedPercent) => {
                  try {
                    await goalsApi.setAchievedPercent(goalId, achievedPercent);

                    // Refresh stats immediately after evaluation
                    fetchDashboardStats();

                    if (currentCycle?._id) {
                      const targetUserId = selectedMember?._id || user?.id;
                      const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
                      if (goalsResponse.success) {
                        setAllGoals(goalsResponse.data || []);
                      }
                    }
                    // Refresh team list to update score on cards
                    if (hasReportees) {
                      setRefreshTeamMembersTrigger(prev => prev + 1);
                    }
                  } catch (error) {
                    console.error('Error setting achieved percent:', error);
                    toast.error('Failed to update achieved percentage');
                  }
                }}
                onCalculateScore={async (cycleId, userId) => {
                  try {
                    const response = await goalsApi.calculateScore(cycleId, userId);
                    if (response.success) {
                      console.log('Final score calculated:', response.data);
                      // Refresh goals to get updated scores
                      const goalsResponse = await goalsApi.getGoals({ cycleId, userId });
                      if (goalsResponse.success) {
                        setAllGoals(goalsResponse.data || []);
                      }
                      // Refresh team members to update scores in Team Member Report
                      if (hasReportees) {
                        setRefreshTeamMembersTrigger(prev => prev + 1);
                      }
                      // After a manager evaluates a team member, drop back to the
                      // team list — same UX as Mark Review Complete.
                      const ownId = (user?.id || user?._id)?.toString();
                      const evaluatedId = userId?.toString();
                      if (selectedMember && evaluatedId && evaluatedId !== ownId) {
                        setSelectedMember(null);
                        setIsManagerDetailView(false);
                        setRefreshTeamMembersTrigger(prev => prev + 1);
                      }
                    }
                  } catch (error) {
                    console.error('Error calculating score:', error);
                    throw error;
                  }
                }}
                cycleId={currentCycle?._id || null}
                targetUserId={selectedMember?._id || selectedMember?.id || user?.id || user?._id || null}
                currentCycle={currentCycle}
                bannerConfig={bannerConfig}
                onSoftSkillRatingChange={handleSoftSkillRatingChange}
                canEditEmployeeRating={false}
                canEditManagerRating={false}
                canEditEmployeeResponse={false}
                canEditManagerResponse={false}
                achievements={achievements.filter(a =>
                  selectedMember ? a.memberId === selectedMember.id && a.month === memberReportMonth : a.month === (selectedMonth || currentCycle?.month)
                )}
                minGoals={goalsSettings?.minGoals || 0}
                maxGoals={goalsSettings?.maxGoals || 0}
                criticalRequired={goalsSettings?.criticalRequired || 0}
                minWeightage={goalsSettings?.minWeightage ?? 10}
                maxWeightage={goalsSettings?.maxWeightage ?? 100}
                inlineNewGoal={inlineNewGoal}
                setInlineNewGoal={setInlineNewGoal}
                editingGoalId={editingGoalId}
                setEditingGoalId={setEditingGoalId}
                onSaveNewGoal={handleSaveNewGoal}
                onSaveEditGoal={handleSaveEditGoal}
                onCancelEdit={handleCancelEdit}
                onSubmitGoals={handleSubmitGoals}
                scoreRanges={goalsSettings.scoreRanges}
                isSubmissionWindowActive={isSubmissionWindowActive}
                isReviewWindowActive={isReviewWindowActive}
                isEvaluationWindowActive={isEvaluationWindowActive}
                badgeRanges={goalsSettings?.badgeRanges || []}
                streakIconUrl={goalsSettings?.streakPoints?.icon?.url || null}
                aiGoalEnabled={!!goalsSettings?.aiGoalEnabled}
                onToggleAiGoal={handleToggleAiGoal}
                projectsList={projectsList}
                cycleScoringMode={currentCycle?.scoringMode || 'legacy'}
                onSelfEvaluate={handleSelfEvaluate}
                onVerifyGoal={handleVerifyGoal}
                pagination={teamMembersPagination}
                currentPage={teamMembersPage}
                onPageChange={(newPage) => {
                  setTeamMembersPage(newPage);
                }}
              />
            )
          ) : (
            // Employee View (Always show logged-in user's dashboard)
            activeTab === 'myGoals' && !isDetailView ? (
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="dashboard-skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <GoalsDashboardSkeleton />
                  </motion.div>
                ) : (
                  <motion.div
                    key="dashboard"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <GoalsDashboard
                      softSkillsEnabled={goalsSettings?.softSkillsEnabled !== false}
                      bannerConfig={bannerConfig}
                      months={months}
                      onMonthClick={handleMonthClick}
                      onAddGoal={() => {
                        // Navigate to active month (February) detail view, same as clicking on the month
                        const activeMonth = months.find(m => m.status === 'active');
                        if (activeMonth) {
                          handleMonthClick(activeMonth.name);
                        }
                      }}
                      employeeAvgScore={employeeAvgScore}
                      onBadgeInfoClick={() => setIsBadgeInfoModalOpen(true)}
                      onStreakInfoClick={() => setIsStreakInfoModalOpen(true)}
                      hasReportees={hasReportees}
                      currentCycle={currentCycle}
                      badgeRanges={goalsSettings?.badgeRanges || []}
                      streakIconUrl={goalsSettings?.streakPoints?.icon?.url || null}
                      isOrgAdmin={isOrgAdmin}
                      isAutonomousMode={isAutonomousMode}
                      scoreBenchmark={scoreBenchmark}
                      goalsCount={allGoals.filter(g => {
                        const goalUserId = (typeof g.userId === 'object' ? g.userId?._id : g.userId)?.toString();
                        const currentUserId = (user?.id || user?._id)?.toString();
                        return goalUserId === currentUserId && g.status !== 'REJECTED';
                      }).length}
                      maxGoals={goalsSettings?.maxGoals || 0}
                      isEvaluationWindowActive={isEvaluationWindowActive}
                      hasGoalsInCurrentCycle={allGoals.some(g => {
                        const goalCycleId = (typeof g.cycleId === 'object' ? g.cycleId?._id : g.cycleId)?.toString();
                        const currentCycleId = currentCycle?._id?.toString();
                        const goalUserId = (typeof g.userId === 'object' ? g.userId?._id : g.userId)?.toString();
                        const currentUserId = (user?.id || user?._id)?.toString();
                        return goalCycleId === currentCycleId && goalUserId === currentUserId && g.status !== 'REJECTED';
                      })}
                      isSubmissionWindowActive={isSubmissionWindowActive}
                      currentGoals={allGoals.filter(g => {
                        const goalCycleId = (typeof g.cycleId === 'object' ? g.cycleId?._id : g.cycleId)?.toString();
                        const goalUserId = (typeof g.userId === 'object' ? g.userId?._id : g.userId)?.toString();
                        const currentUserId = (user?.id || user?._id)?.toString();
                        return goalCycleId === currentCycle?._id?.toString() && goalUserId === currentUserId && g.status !== 'REJECTED';
                      })}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
                      <div className="lg:col-span-2">
                        <PerformanceTrendsCard canToggleTeam={hasReportees || isOrgAdmin} />
                      </div>
                      <div className="lg:col-span-1 flex flex-col gap-4">
                        <GoalsScoreboard maxScore={currentCycle?.scoringMode === 'star5' ? 5 : 10} />
                        <RecentActivityCard canToggleTeam={hasReportees || isOrgAdmin} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            ) : (
              (() => {
                // Calculate editability flags for soft skills and review summary
                const isViewingTeamMember = selectedMember && selectedMember._id && selectedMember._id !== (user?.id || user?._id);
                // Soft Skills editability - Employee can edit THEIR OWN ratings during Submission Window, Or Org Admin in autonomous mode for themselves
                const canEditEmployeeRating = (!isViewingTeamMember && isSubmissionWindowActive) || (isOrgAdmin && isAutonomousMode && !isViewingTeamMember);
                // Manager can edit manager rating for team members during Evaluation Window, Or Org Admin in autonomous mode for themselves
                const canEditManagerRating = (hasReportees && isViewingTeamMember && isEvaluationWindowActive) || (isOrgAdmin && isAutonomousMode && !isViewingTeamMember);
                // Review Summary editability
                const canEditEmployeeResponse = (!isViewingTeamMember && isEvaluationWindowActive) || (isOrgAdmin && isAutonomousMode && !isViewingTeamMember); // Employee can edit their own response
                const canEditManagerResponse = (hasReportees && isViewingTeamMember && isEvaluationWindowActive) || (isOrgAdmin && isAutonomousMode && !isViewingTeamMember); // Manager can edit manager response for team members

                return (
                  <GoalsDetailView
                    softSkillsEnabled={goalsSettings?.softSkillsEnabled !== false}
                    isViewedMonthCycleEvaluated={isViewedMonthCycleEvaluated}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    bannerConfig={bannerConfig}
                    goals={detailViewGoals}
                    softSkillsData={softSkillsData}
                    reviewSummary={reviewSummary}
                    isReviewSummaryExpanded={isReviewSummaryExpanded}
                    onToggleReviewSummary={() => setIsReviewSummaryExpanded(!isReviewSummaryExpanded)}
                    onReviewChange={handleReviewChange}
                    onBack={async () => {
                      setIsDetailView(false);
                      setSelectedMonth(null);
                      // Refresh team list + KPIs in case detail-view edits changed anything
                      setRefreshTeamMembersTrigger(prev => prev + 1);
                      // Restore currentCycle to actual current cycle when going back
                      try {
                        const response = await goalsApi.getCurrentCycle();
                        if (response?.success && response?.data) {
                          const cycleData = Array.isArray(response.data) ? response.data[0] : response.data;
                          if (cycleData && cycleData.month && cycleData.status) {
                            setCurrentCycle(cycleData);
                            setSelectedMonth(cycleData.month);
                            const year = cycleData.year;
                            setSelectedYear(year != null ? year.toString() : new Date().getFullYear().toString());
                          }
                        }
                      } catch (error) {
                        console.error('Error restoring current cycle:', error);
                        // Fallback: try to find current cycle from allCycles
                        const currentCycleFromAll = allCycles.find(c => c.status === 'active');
                        if (currentCycleFromAll) {
                          setCurrentCycle(currentCycleFromAll);
                          setSelectedMonth(currentCycleFromAll.month);
                          const year = currentCycleFromAll.year;
                          setSelectedYear(year != null ? year.toString() : new Date().getFullYear().toString());
                        }
                      }
                    }}
                    onAddGoal={() => setIsModalOpen(true)}
                    onApproveGoal={handleApproveGoal}
                    onRejectGoal={handleRejectGoal}
                    onDeleteGoal={handleDeleteGoal}
                    onEditGoal={handleEditGoal}
                    currentUserId={user?.id || user?._id || null}
                    targetUserId={selectedMember?._id || selectedMember?.id || user?.id || user?._id || null}
                    cycleId={currentCycle?._id || null}
                    onCalculateScore={async (cycleId, userId) => {
                      try {
                        const response = await goalsApi.calculateScore(cycleId, userId);
                        if (response.success) {
                          console.log('Final score calculated:', response.data);
                          // Refresh goals to get updated scores
                          const goalsResponse = await goalsApi.getGoals({ cycleId, userId });
                          if (goalsResponse.success) {
                            setAllGoals(goalsResponse.data || []);
                          }
                          // Refresh team members to update scores in Team Member Report
                          if (hasReportees) {
                            setRefreshTeamMembersTrigger(prev => prev + 1);
                          }
                        }
                      } catch (error) {
                        console.error('Error calculating score:', error);
                        throw error;
                      }
                    }}
                    loading={loading}
                    softSkillsLoading={softSkillsLoading}
                    reviewSummaryLoading={reviewSummaryLoading}
                    onSetAchievedPercent={async (goalId, achievedPercent, skipRefresh = false) => {
                      try {
                        // Only call API if both goalId and achievedPercent are provided and valid
                        // achievedPercent = -1 is a special flag for refresh-only (no API call)
                        if (goalId && achievedPercent !== null && achievedPercent !== undefined && achievedPercent !== -1) {
                          await goalsApi.setAchievedPercent(goalId, achievedPercent);
                          // Refresh dashboard stats after update
                          fetchDashboardStats();
                          // Refresh team list to update score on cards
                          if (hasReportees) {
                            setRefreshTeamMembersTrigger(prev => prev + 1);
                          }
                        }

                        // Only refresh goals if not in batch mode (skipRefresh = false)
                        // This ensures we refresh once at the end of batch evaluation, not after each goal
                        if (!skipRefresh && currentCycle?._id) {
                          const targetUserId = selectedMember?._id || user?.id;
                          const goalsResponse = await goalsApi.getGoals({ cycleId: currentCycle._id, userId: targetUserId });
                          if (goalsResponse.success) {
                            setAllGoals(goalsResponse.data || []);
                          }
                        }
                        // Toast removed - single toast will be shown in GoalsTable after all goals are evaluated
                      } catch (error) {
                        console.error('Error setting achieved percent:', error);
                        // Don't show individual error toasts - let GoalsTable handle it
                        throw error;
                      }
                    }}
                    onSoftSkillRatingChange={handleSoftSkillRatingChange}
                    canEditEmployeeRating={canEditEmployeeRating}
                    canEditManagerRating={canEditManagerRating}
                    canEditEmployeeResponse={canEditEmployeeResponse}
                    canEditManagerResponse={canEditManagerResponse}
                    achievements={achievements.filter(a => a.month === selectedMonth)}
                    onAddAchievement={() => setIsAchievementModalOpen(true)}
                    isGoalsCycleEditable={isGoalsCycleEditable}
                    isSubmissionWindowActive={isSubmissionWindowActive}
                    isReviewWindowActive={isReviewWindowActive}
                    isOrgAdmin={isOrgAdmin}
                    isAutonomousMode={isAutonomousMode}
                    isEvaluationWindowActive={isEvaluationWindowActive}
                    isViewingTeamMember={isViewingTeamMember}
                    onBadgeInfoClick={() => setIsBadgeInfoModalOpen(true)}
                    onStreakInfoClick={() => setIsStreakInfoModalOpen(true)}
                    inlineNewGoal={inlineNewGoal}
                    setInlineNewGoal={setInlineNewGoal}
                    editingGoalId={editingGoalId}
                    setEditingGoalId={setEditingGoalId}
                    onSaveNewGoal={handleSaveNewGoal}
                    onSaveEditGoal={handleSaveEditGoal}
                    onCancelEdit={handleCancelEdit}
                    minGoals={goalsSettings?.minGoals || 0}
                    maxGoals={goalsSettings?.maxGoals || 0}
                    criticalRequired={goalsSettings?.criticalRequired || 0}
                    minWeightage={goalsSettings?.minWeightage ?? 10}
                    maxWeightage={goalsSettings?.maxWeightage ?? 100}
                    isManager={hasReportees}
                    currentCycle={currentCycle}
                    badgeRanges={goalsSettings?.badgeRanges || []}
                    streakIconUrl={goalsSettings?.streakPoints?.icon?.url || null}
                    employeeAvgScore={employeeAvgScore}
                    onSubmitGoals={handleSubmitGoals}
                    scoreRanges={goalsSettings.scoreRanges}
                    onToggleCritical={handleToggleCritical}
                    aiGoalEnabled={!!goalsSettings?.aiGoalEnabled}
                    onToggleAiGoal={handleToggleAiGoal}
                    allCycles={allCycles}
                    projectsList={projectsList}
                    cycleScoringMode={currentCycle?.scoringMode || 'legacy'}
                    onSelfEvaluate={handleSelfEvaluate}
                    onVerifyGoal={handleVerifyGoal}
                  />
                );
              })()
            )
          )}
        </div>
      </section>

      {/* Add/Edit Goal Modal */}
      <AddGoalModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingGoal(null);
        }}
        onSubmit={handleAddGoal}
        goalType={goalType}
        setGoalType={setGoalType}
        goal={editingGoal}
        minWeightage={goalsSettings?.minWeightage ?? 10}
        maxWeightage={goalsSettings?.maxWeightage ?? 100}
        projectsList={projectsList}
      />

      {/* Add Achievement Modal */}
      <AddAchievementModal
        isOpen={isAchievementModalOpen}
        onClose={() => setIsAchievementModalOpen(false)}
        onSubmit={handleAddAchievement}
      />

      {/* Badge Info Modal */}
      <BadgeInfoModal
        isOpen={isBadgeInfoModalOpen}
        onClose={() => setIsBadgeInfoModalOpen(false)}
        currentBadge={dashboardStats?.badge || calculateBadgeProgress.progressStart}
        nextBadge={calculateBadgeProgress.nextBadge || calculateBadgeProgress.progressLabel}
        badgeRanges={goalsSettings?.badgeRanges || []}
        avgScore={dashboardStats?.avgScore ? parseFloat(dashboardStats.avgScore) : null}
      />

      {/* Streak Info Modal */}
      <StreakInfoModal
        isOpen={isStreakInfoModalOpen}
        onClose={() => setIsStreakInfoModalOpen(false)}
        streak={dashboardStats?.streak || 0}
        streakIconUrl={goalsSettings?.streakPoints?.icon?.url || null}
      />

      {/* Goals cycle submit footer — portal target for GoalsTable's submit/compliance bar.
          Uses position: fixed pinned to viewport bottom; left/width are sized via the
          FooterSlotPortal helper to track the scroll container's bounding rect so the bar
          aligns with main content (and doesn't run under a sidebar). Empty until a child
          mounts; rendered as fixed regardless of where the user has scrolled. */}
      <FooterSlotPortal
        scrollRef={scrollContainerRef}
        className={`fixed bottom-0 z-30 backdrop-blur-md empty:hidden ${isDark
          ? 'bg-[#0b1020]/90 border-t border-white/10 shadow-[0_-6px_24px_rgba(0,0,0,0.4)]'
          : 'bg-white/95 border-t border-slate-200 shadow-[0_-6px_24px_rgba(15,23,42,0.06)]'
          }`}
      />

      <Modal
        title="Reject Goal"
        open={rejectModal.open}
        onOk={submitRejectModal}
        onCancel={() => {
          if (isRejectingGoal) return;
          setRejectModal({ open: false, goalId: null, goalName: '', reason: '' });
        }}
        okText="Reject Goal"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: isRejectingGoal, disabled: !rejectModal.reason || !rejectModal.reason.trim() }}
        cancelButtonProps={{ disabled: isRejectingGoal }}
        destroyOnClose
        maskClosable={!isRejectingGoal}
      >
        <p style={{ marginBottom: 8 }}>
          {rejectModal.goalName
            ? <>Why are you rejecting <strong>"{rejectModal.goalName}"</strong>? This reason is sent to the team member.</>
            : <>Why are you rejecting this goal? This reason is sent to the team member.</>
          }
        </p>
        <Input.TextArea
          autoFocus
          rows={4}
          value={rejectModal.reason}
          onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
          placeholder="e.g. Target metric is too vague — please define how success will be measured."
          disabled={isRejectingGoal}
        />
      </Modal>

      {isOrgAdmin && (
        <Drawer
          open={aiGoalsDrawerOpen}
          onClose={() => setAiGoalsDrawerOpen(false)}
          title="AI Goals"
          subtitle={
            selectedAiGoalsCycle
              ? `${selectedAiGoalsCycle.label} · ${aiGoalsLoading ? '…' : `${filteredAiGoals.length} marked`}`
              : 'Months with AI goals only'
          }
          icon={<BrainCircuit size={18} />}
          width={520}
          toolbar={
            !aiGoalsLoading && aiGoalsCycleOptions.length > 0 ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: t.s[2] }}>
                  <div style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.c.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Month
                  </div>
                  <V2Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download size={13} />}
                    onClick={downloadAiGoalsCsv}
                    disabled={!filteredAiGoals.length}
                  >
                    Download
                  </V2Button>
                </div>
                <Select
                  value={aiGoalsCycleId || undefined}
                  onChange={handleAiGoalsCycleChange}
                  options={aiGoalsCycleOptions}
                  placeholder="Select month"
                  suffixIcon={<Calendar size={14} />}
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  popupMatchSelectWidth
                  getPopupContainer={() => document.body}
                  dropdownStyle={{ zIndex: 10050 }}
                />
              </div>
            ) : null
          }
        >
          {aiGoalsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 48, color: t.c.textTertiary }}>
              <Loader2 size={18} className="dashv2-spin" />
              Loading AI goals…
            </div>
          ) : filteredAiGoals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: t.c.textTertiary }}>
              <BrainCircuit size={28} style={{ marginBottom: 12, opacity: 0.4 }} />
              <div style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.textSecondary, marginBottom: 4 }}>
                No AI goals yet
              </div>
              <div style={{ fontSize: t.font.size.sm }}>
                Months appear here once team members mark an AI Goal.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[3] }}>
              {filteredAiGoals.map((goal) => {
                const member = goal.userId || {};
                const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || 'Unknown';
                const designation = member.designation || member.position || member.department || '';
                const statusColor =
                  goal.status === 'APPROVED' ? t.status.positive
                    : goal.status === 'REJECTED' ? t.status.negative
                      : t.status.warning;
                return (
                  <div
                    key={goal._id}
                    style={{
                      padding: t.s[4],
                      borderRadius: t.r.md,
                      border: `1px solid ${t.c.border}`,
                      background: t.c.surfaceMuted,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>{name}</div>
                        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>
                          {[member.email, designation].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <span style={{
                        fontSize: t.font.size.xs, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                        background: `${statusColor}22`, color: statusColor, flexShrink: 0, textTransform: 'capitalize',
                      }}>
                        {(goal.status || 'pending').toLowerCase()}
                      </span>
                    </div>
                    <div style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.text, marginBottom: 4 }}>
                      {goal.name}
                    </div>
                    {goal.metric && (
                      <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginBottom: 8 }}>
                        Metric: {goal.metric}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: t.font.size.xs, color: t.c.textTertiary }}>
                      {goal.weightage != null && <span>Weight {goal.weightage}%</span>}
                      {goal.projectId?.name && <span>· {goal.projectId.name}</span>}
                      {goal.critical && <span>· Critical</span>}
                      {goal.achievedPercent != null && <span>· Achieved {goal.achievedPercent}%</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Drawer>
      )}
      </div>
    </PageShell>
  );
};

export default GoalsV2;
